import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Briefcase, FileText, Shield, DollarSign,
  Calendar, MapPin, Hash, Upload, ExternalLink, Edit3, Navigation,
  TrendingUp, Paperclip, Download, Trash2, AlertTriangle,
  ChevronRight, ChevronDown, BookOpen, Flag, Clock, X, DraftingCompass, CalendarRange, Truck, FolderOpen, Wrench, Wallet,
} from "lucide-react";
import { exportProjectFinancialsXlsx, exportProjectFinancialsPdf } from "./projectFinancialsExport";
import { generateComponentStatementPdf, type ComponentStatementLine } from "./componentStatementPdf";
import { generateBrokerSowPdf, type BrokerSowSections } from "./brokerSowPdf";
import { loadDesignBundle, designDigest } from "./DesignPanel";
import MilestonesTab from "./MilestonesTab";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, Select } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { distanceFromBneGpoKm } from "@/lib/geo";
import { DistanceFromBne } from "@/components/DistanceFromBne";
// ClientPoCard import removed 2026-05-07 — Financials tab is the single source for PO display now.
import OrderRegisterCard from "@/features/quotes/OrderRegisterCard";   // 27 Oct 2026 — the STOW / PO register (mig 454/462)
import ProjectBudgetTab from "./ProjectBudgetTab";   // 28 Oct 2026 — the manager's budget, its own tab (mig 470–472)
import ProjectCrewAssignmentsCard from "./ProjectCrewAssignmentsCard";
import ProjectDeliverablesCard from "./ProjectDeliverablesCard";
import ProjectIndividualAssignmentsCard from "./ProjectIndividualAssignmentsCard";
import ProjectTeamCard from "./ProjectTeamCard";
import ProjectNotepadCard from "./ProjectNotepadCard";
import ProjectAboutCard from "./ProjectAboutCard";   // 4 Nov 2026 — mig 507: carrier, program, work type, the client's PE
import ProjectDiaryTab from "@/features/diary/ProjectDiaryTab";
import ProjectMaterialsTab from "@/features/materials/ProjectMaterialsTab";
import SiteFolderSetup from "@/features/sitefolder/SiteFolderSetup";   // mig 393 — the Site folder tab
import LibraryDocumentsTab from "./LibraryDocumentsTab";
import JobFolderCard from "@/components/JobFolderCard";
import { ProjectHseTab } from "@/features/hse/ProjectHseTab";
import DesignPanel from "./DesignPanel";
import ProgramPanel from "./ProgramPanel";
import { ProjectStanddownBanner } from "@/features/hse/standdownShared";   // mig 425 — stood down / embargo / sister-site flag
import QuotePdfButton from "@/features/quotes/QuotePdfButton";   // 6 Oct 2026 — the sent quote PDF, right here
import ProjectDefectsTab, { useProjectDefectBadge, DefectCountBadge } from "@/features/defects/ProjectDefectsTab";   // 9 Oct 2026

// =============================================================================
// ProjectDetailScreen v3 (22 Apr 2026)
// =============================================================================
// Adds:
//   - sites(*) query so we grab address/lat/lng for the Site card
//   - Brisbane GPO distance shown under the address (mob-distance proxy)
//   - source_quote_id queried + fetched + rendered on Financials tab as a
//     clickable link — "From Q-26-0001 · $25,923 ex GST"
//   - Documents tab wired to the bid_documents table filtered by project_id;
//     shows files carried over from the source BID plus any uploaded directly
//     against this project
//   - "Promoted from source BID" already existed on Overview — unchanged
//
// 9 Oct 2026:
//   - Defects tab (features/defects/ProjectDefectsTab) — this project's
//     defects without a trip to the Defect Register. The tab carries a count
//     and a colour: green none open · amber open, every one with an ETA still
//     ahead · red any open with no ETA or past it.
//   - ?tab= now honours every tab (design/program/milestones/folder were
//     silently ignored — the quote screen's "open design" link landed on
//     Overview).
//   - Financials → Source documents lists EXTRA standard quotes accepted onto
//     the project (mig 375/439), not only variations. They were in the
//     contract value but invisible here, and missing from the crew-days
//     tracker and the component statement.
// =============================================================================

type TabKey = "overview" | "folder" | "design" | "program" | "budget" | "milestones" | "documents" | "hse" | "financials" | "diary" | "materials" | "defects";
const TAB_KEYS: TabKey[] = ["overview", "folder", "design", "program", "budget", "milestones", "documents", "hse", "financials", "diary", "materials", "defects"];

const PROJECT_STATUSES = [
  "scheduled",
  "mobilising",
  "live",
  "on_hold",
  "acceptance_pending",
  "closed",
] as const;

type ProjectStatus = typeof PROJECT_STATUSES[number];

export function ProjectDetailScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Initial tab honours ?tab=<key> if present in URL (used by Site Diary
  // post-submit redirect to land on the diary tab). Falls back to overview.
  const urlTab = searchParams.get("tab") as TabKey | null;
  const [tab, setTab] = useState<TabKey>(
    urlTab && TAB_KEYS.includes(urlTab)
      ? urlTab
      : "overview"
  );
  useEffect(() => {
    if (urlTab && TAB_KEYS.includes(urlTab)) {
      setTab(urlTab as TabKey);
    }
  }, [urlTab]);

  // 9 Oct 2026 — the Defects tab's count + colour (hook: above the early returns)
  const defectBadge = useProjectDefectBadge(projectId);

  const { data: project, isLoading, refetch } = useQuery({
    queryKey: ["project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      // (supabase as any) — hse_system / hse_client_platform (mig 392) aren't
      // in database.types.ts yet; the row was already read as `any` below.
      const { data, error } = await (supabase as any)
        .from("projects")
        .select(`
          project_id, project_code, project_name, status, project_type, sharepoint_folder_url,
          client_id, carrier_id, program, client_pe_contact_id,
          region_code, contract_value, access_tail_days,
          client_po_number, client_po_value, client_po_date,
          scheduled_start_date, scheduled_end_date,
          actual_start_date, actual_end_date,
          working_height_m,
          invoiced_to_date, paid_to_date, notes,
          is_high_risk_construction_work, requires_jmp, risk_rating,
          sharepoint_folder_url, source_bid_id, source_quote_id,
          hse_system, hse_client_platform,
          project_manager_id, supervisor_id, site_lead_id,
          project_manager:project_manager_id(employee_id, first_name, last_name, primary_role_name, mobile),
          supervisor:supervisor_id(employee_id, first_name, last_name, primary_role_name, mobile),
          site_lead:site_lead_id(employee_id, first_name, last_name, primary_role_name, mobile),
          clients(client_code, legal_name),
          sites(*, asset_owner:asset_owners(owner_code, display_name)),
          carriers(carrier_code)
        `)
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!project) return <div className="p-6">Project not found.</div>;

  const canManage = hasRole(user, "manager");

  return (
    <div className="p-4 md:p-6 pb-24 max-w-[1200px] mx-auto">
      <button
        onClick={() => navigate("/projects")}
        className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4"
      >
        <ArrowLeft size={14}/> Projects
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] text-accent">{project.project_code}</span>
            <StatusPill status={project.status} />
            {project.is_high_risk_construction_work && <Pill label="HRCW" tone="critical" small />}
            {project.requires_jmp && <Pill label="JMP required" tone="bid" small />}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mt-1">{project.project_name}</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {project.clients?.legal_name}
            {project.carriers?.carrier_code && ` · ${project.carriers.carrier_code}`}
            {project.sites?.site_code && ` · ${project.sites.site_code}`}
            {project.region_code && ` · ${project.region_code}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/projects/${project.project_id}/schedule`}>
            <Button variant="secondary" size="sm">Schedule</Button>
          </Link>
          <Link to={`/projects/${project.project_id}/hop`}>
            <Button variant="secondary" size="sm">HOP library</Button>
          </Link>
          {/* 2 Nov 2026 — a quote or variation straight from the job; Finance → Quotes → New stays for standalone quotes */}
          {canManage && <Link to={`/quotes/new?project=${project.project_id}&mode=project`}><Button variant="secondary" size="sm">New quote</Button></Link>}
          {canManage && <Link to={`/quotes/new?project=${project.project_id}&mode=variation`}><Button variant="secondary" size="sm">Variation</Button></Link>}
          {/* "Folder template" button removed 27 Sep 2026 — James: "delete it,
              not needed and it's old ways of working". Job folders are the
              gateway's SharePoint skeleton now. */}
          {canManage && <ChangeStatusButton project={project} onChanged={refetch} />}
        </div>
      </div>

      {/* Mig 425 (8 Oct 2026) — red when this project is stood down or its
          carrier has an embargo; amber when a sister site under the same
          client / carrier is. Above the tabs so it shows on every one. */}
      <ProjectStanddownBanner projectId={project.project_id} canOpen className="mb-4" />

      <div className="flex items-center gap-1 mb-5 border-b border-line overflow-x-auto">
        {([
          ["overview",   "Overview",   Briefcase],
          ["folder",     "Site folder", FolderOpen],
          ["design",     "Design",     DraftingCompass],
          ...(canManage ? [["program", "Program", CalendarRange] as const] : []),
          ...(canManage ? [["budget", "Budget", Wallet] as const] : []),   // 28 Oct 2026 — the manager's budget (mig 470–472)
          ["milestones", "Milestones", Flag],
          ["documents",  "Documents",  FileText],
          ["hse",        "HSE",        Shield],
          ["diary",      "Site Diary", BookOpen],
          ["materials",  "Materials",  Truck],
          ["defects",    "Defects",    Wrench],
          ...(canManage ? [["financials", "Financials", DollarSign] as const] : []),
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key as TabKey)}
            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
              tab === key ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text"
            }`}
          >
            <Icon size={13} />
            {label}
            {key === "defects" && <DefectCountBadge badge={defectBadge} />}
          </button>
        ))}
      </div>

      {tab === "overview"   && <OverviewTab project={project} canManage={canManage} onProjectChanged={refetch} />}
      {tab === "folder"     && <SiteFolderSetup projectId={project.project_id} />}
      {tab === "design"     && <DesignPanel parent={{ kind: "project", id: project.project_id, code: project.project_code, name: project.project_name, siteId: (project as any).site_id ?? null }} canEdit={canManage} />}
      {tab === "program"    && canManage && <ProgramPanel parent={{ kind: "project", id: project.project_id, code: project.project_code, name: project.project_name }} preparedBy={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Coretel Group"} siteLine={(project as any).site?.site_name ?? null} />}
      {tab === "budget"     && canManage && <ProjectBudgetTab project={project} />}
      {tab === "milestones" && <MilestonesTab project={project} canManage={canManage} />}
      {tab === "diary"      && <ProjectDiaryTab projectId={project.project_id} />}
      {tab === "materials"  && <ProjectMaterialsTab projectId={project.project_id} />}
      {tab === "defects"    && <ProjectDefectsTab projectId={project.project_id} canManage={canManage} />}
      {tab === "documents"  && <LibraryDocumentsTab project={project} user={user} canEdit={canManage} />}
      {tab === "hse"        && <ProjectHseTab project={project} />}
      {tab === "financials" && canManage && <FinancialsTab project={project} />}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "live"               ? "won" :
    status === "acceptance_pending" ? "won" :
    status === "scheduled"          ? "bid" :
    status === "mobilising"         ? "bid" :
    status === "on_hold"            ? "critical" :
    status === "closed"             ? "lost" :
    status === "cancelled"          ? "lost" :
    "bid";
  return <Pill label={status.replace(/_/g, " ")} tone={tone as any} />;
}

function ChangeStatusButton({ project, onChanged }: { project: any; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // James, 21 Aug 2026: "this Yarraman site is pissing me off, it's a dud site,
  // we never got it, I force closed it but got stuck, let's delete it."
  //
  // It was not stuck and it did not need deleting. The crew schedule fetches
  // projects under the key ["schedule_projects"] and only asks for live-ish
  // statuses, so closing Yarraman genuinely did take it out of the query - but
  // nothing here ever told react-query the answer had changed, so the schedule
  // kept serving the cached list it had already fetched. Close a project, walk
  // to the schedule, and there it still was.
  //
  // Refetching the project alone was never going to fix that: it is a different
  // query. Every cached view of the project list has to be told.
  const qcStatus = useQueryClient();
  // T-290 — when user picks 'closed', we open a small date-capture panel
  // instead of firing the mutation directly. The check constraint
  // projects_status_acceptance_ck requires acceptance_received_at to be set
  // before status can move to 'closed'. Default the date input to today, let
  // the user override if they know the real acceptance date.
  const [closeDate, setCloseDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [closeMode, setCloseMode] = useState<boolean>(false);

  const mut = useMutation({
    mutationFn: async (args: { newStatus: ProjectStatus; acceptanceDate?: string }) => {
      const updates: any = { status: args.newStatus };
      const today = new Date().toISOString().slice(0, 10);
      if (args.newStatus === "live"   && !project.actual_start_date) updates.actual_start_date = today;
      if (args.newStatus === "closed" && !project.actual_end_date)   updates.actual_end_date   = today;
      // T-290 — populate acceptance_received_at on close, satisfies the
      // check constraint. If the project already has one, keep it (don't
      // overwrite — real acceptance date trumps a manual fallback).
      if (args.newStatus === "closed" && !project.acceptance_received_at && args.acceptanceDate) {
        updates.acceptance_received_at = args.acceptanceDate;
      }
      const { error } = await supabase.from("projects").update(updates).eq("project_id", project.project_id);
      if (error) throw error;
    },
    onSuccess: () => {
      setError(null); setOpen(false); setCloseMode(false);
      // Every list that filters on status. Without these a closed project keeps
      // showing on the schedule until a hard refresh.
      qcStatus.invalidateQueries({ queryKey: ["schedule_projects"] });
      qcStatus.invalidateQueries({ queryKey: ["schedule_pcas"] });
      qcStatus.invalidateQueries({ queryKey: ["projects"] });
      onChanged();
    },
    // T-251 — without this, backend errors (RLS reject, trigger raise, check
    // constraint, etc.) vanished silently. mig 126's broken trigger went
    // undetected for 8 days because of it. Always surface the error so the
    // next regression announces itself loudly.
    onError: (e: any) => setError(`Couldn't change status: ${e?.message ?? e}`),
  });

  function pickStatus(s: ProjectStatus) {
    setError(null);
    // T-290 — closing requires the date capture step. Every other transition
    // fires immediately as before.
    if (s === "closed") {
      setCloseMode(true);
    } else {
      mut.mutate({ newStatus: s });
    }
  }

  function confirmClose() {
    if (!closeDate) { setError("Pick an acceptance date."); return; }
    mut.mutate({ newStatus: "closed", acceptanceDate: closeDate });
  }

  return (
    <div className="relative">
      <Button
        icon={Edit3}
        onClick={() => { setError(null); setCloseMode(false); setOpen(v => !v); }}
        variant="secondary"
      >
        Change status
      </Button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => { setOpen(false); setCloseMode(false); }}
          />
          {!closeMode ? (
            // Status picker (default state)
            <div className="absolute right-0 top-full mt-1 bg-ink-card border border-line rounded-lg shadow-lg min-w-[200px] z-20">
              {PROJECT_STATUSES.filter(s => s !== project.status).map(s => (
                <button
                  key={s}
                  onClick={() => pickStatus(s)}
                  disabled={mut.isPending}
                  className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-ink-raised font-mono first:rounded-t-lg last:rounded-b-lg disabled:opacity-50"
                >
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          ) : (
            // T-290 — close-with-date capture panel
            <div className="absolute right-0 top-full mt-1 bg-ink-card border border-line rounded-lg shadow-lg w-[320px] z-20 p-4">
              <div className="text-[12px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
                Close project
              </div>
              <div className="text-[13px] text-text mb-3">
                When did the client accept this project?
              </div>
              <label className="block mb-2">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
                  Acceptance date
                </span>
                <input
                  type="date"
                  value={closeDate}
                  onChange={e => setCloseDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]"
                />
              </label>
              <p className="text-[11px] text-text-muted mb-3">
                Defaults to today. Use the real client sign-off date if you have it — it'll
                show in the project Overview and reports.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { setCloseMode(false); setError(null); }}
                  type="button"
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={confirmClose}
                  disabled={mut.isPending || !closeDate}
                  type="button"
                >
                  {mut.isPending ? "Closing…" : "Close project"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {error && (
        <div className="absolute right-0 top-full mt-1 z-30 max-w-[320px] px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-[12px] text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Overview tab — enriched site card with address + BNE distance
// =============================================================================
function OverviewTab({ project, canManage, onProjectChanged }: {
  project: any;
  canManage: boolean;
  onProjectChanged: () => void;
}) {
  // outstanding variable removed 2026-05-07 — was only used in the now-deleted Commercial card.
  const [editDates, setEditDates] = useState(false);
  return (
    <div className="space-y-4">
      {/* T-150 — private/team working notepad + per-user todos.
          Sits above Schedule so it's the first thing each user sees. The
          card renders nothing for subbie role until subbie auth lands. */}
      <ProjectNotepadCard
        projectId={project.project_id}
        projectCode={project.project_code}
      />

      {/* 4 Nov 2026 — mig 507. James: every project carries its carrier; the client's PE is assigned here. */}
      <ProjectAboutCard project={project} canManage={canManage} onChanged={onProjectChanged} />

      {/* T-506 — the job's SharePoint working folder, named from ops */}
      <JobFolderCard
        kind="project"
        id={project.project_id}
        folderUrl={(project as any).sharepoint_folder_url ?? null}
        canManage={canManage}
        onChanged={onProjectChanged}
        sourceBidId={project.source_bid_id ?? null}   // 25 Sep 2026 — offer to move the bid's folder here
      />
      <Card>
        <div className="p-5">
          {/* Schedule heading row — always visible. Manager+ get an "Edit dates"
              button that pops the EditScheduleDialog. T-077 (James 2026-04-25). */}
          <div className="flex items-center justify-between gap-2">
            <SectionHeading icon={Calendar}>Schedule</SectionHeading>
            {canManage && (
              <button
                onClick={() => setEditDates(true)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-accent hover:bg-ink-hover transition-colors"
                title="Edit project dates"
              >
                <Edit3 size={11}/> Edit dates
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
            <DateCell label="Scheduled start" value={project.scheduled_start_date} />
            <DateCell label="Scheduled end"   value={project.scheduled_end_date} />
            <DateCell label="Actual start"    value={project.actual_start_date} />
            <DateCell label="Actual end"      value={project.actual_end_date} />
            {/* T-290 — surfaces the client sign-off date so it's visible
                without opening the edit dialog. Required to be set before
                project can move to 'closed' (DB constraint). */}
            <DateCell label="Client accepted" value={project.acceptance_received_at} />
          </div>
          {/* Working height — T-144. Optional, set when known. Useful for
              crew + machine planning (cranes, EWPs, harness classifications). */}
          {project.working_height_m != null && (
            <div className="mt-3 pt-3 border-t border-line">
              <Kv
                label="Working height"
                value={`${Number(project.working_height_m).toFixed(2)} m`}
                mono
              />
            </div>
          )}
          {/* Helper text — actuals auto-fill on status changes (live → start,
              closed → end), but are manually editable here too. T-290: client
              accepted date can be set here OR captured at close-time via the
              Change status → Close project modal. */}
          {canManage && (
            <div className="mt-3 pt-3 border-t border-line text-[10px] text-text-dim leading-snug">
              Actuals auto-fill when status moves to <span className="font-mono">live</span> (start) or <span className="font-mono">closed</span> (end).
              Client accepted date is captured at close-time, or editable above.
            </div>
          )}

          {/* Milestones — T-079. Free-text name + date, mgr+ can add/remove.
              Surfaces on the project Schedule calendar alongside bookings. */}
          <ProjectMilestones projectId={project.project_id} canManage={canManage} />
        </div>
      </Card>
      {editDates && (
        <EditScheduleDialog
          project={project}
          onClose={() => setEditDates(false)}
          onSaved={() => { setEditDates(false); onProjectChanged(); }}
        />
      )}

      {/* Commercial section removed 2026-05-07 (James) — duplicated by the
          Financials tab which is the single source of truth for contract
          value, invoiced, paid, outstanding, and client PO. Keeping the
          Overview lean. */}

      {project.sites && <SiteCard site={project.sites} projectId={project.project_id} canManage={canManage} onChanged={onProjectChanged} />}

      {/* T-146 — Project-level role slots with inline edit pickers.
          Hidden for non-managers when nothing is assigned (matches old
          behaviour); always visible for managers so they can staff up. */}
      <ProjectTeamCard project={project as any} onSaved={onProjectChanged}/>

      {/* Crew assignments — T-125. Lists current + past crews on this
          project; manager+ can add/end assignments inline. */}
      <ProjectCrewAssignmentsCard
        projectId={project.project_id}
        projectScheduledStart={project.scheduled_start_date ?? null}
        projectScheduledEnd={project.scheduled_end_date ?? null}
      />

      <ProjectDeliverablesCard projectId={project.project_id} />

      {/* Individual ad-hoc assignments — T-131. Sits next to the crew card
          for one-off / labour-hire / single-person assignments outside of
          a standing crew. Uses the existing project_team table. */}
      <ProjectIndividualAssignmentsCard
        projectId={project.project_id}
        projectScheduledEnd={project.scheduled_end_date ?? null}
      />

      {project.notes && (
        <Card>
          <div className="p-5">
            <SectionHeading>Notes</SectionHeading>
            <div className="text-[13px] text-text mt-2 whitespace-pre-wrap">{project.notes}</div>
          </div>
        </Card>
      )}

      {/* Lineage footer */}
      {(project.source_bid_id || project.source_quote_id) && (
        <div className="text-[12px] text-text-muted flex items-center gap-4 flex-wrap">
          {project.source_bid_id && (
            <Link to={`/bids/${project.source_bid_id}`} className="text-accent hover:underline inline-flex items-center gap-1">
              <TrendingUp size={11}/> Promoted from source BID
            </Link>
          )}
          {project.source_quote_id && (
            <Link to={`/quotes/${project.source_quote_id}`} className="text-accent hover:underline inline-flex items-center gap-1">
              <FileText size={11}/> From accepted quote
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Site card with address + Brisbane GPO distance
// -----------------------------------------------------------------------------
function SiteCard({ site, projectId, canManage, onChanged }: { site: any; projectId?: string; canManage?: boolean; onChanged?: () => void }) {
  // 3 Nov 2026 — James, Eltham - Audit: "I selected the wrong site when I
  // created the site." There was no way to change a project's site after
  // creation. Manager+: pick another site, confirm, projects.site_id moves.
  // Bookings, diary and documents stay on the project; the site folder link
  // is the site's own, so a wrong-site folder is re-linked on the Site folder tab.
  const [changing, setChanging] = useState(false);
  const lat = site?.latitude  ?? site?.lat  ?? null;
  const lng = site?.longitude ?? site?.lng  ?? null;
  const km  = distanceFromBneGpoKm(lat, lng);

  const address  = site?.address_line1 ?? site?.street_address ?? site?.address ?? null;
  const suburb   = site?.suburb ?? site?.locality ?? null;
  const state    = site?.state  ?? site?.region   ?? null;
  const postcode = site?.postcode ?? site?.post_code ?? null;
  const w3w      = site?.what3words ?? null;

  // Regional sites usually have the whole address typed into line 1 with suburb
  // and postcode left blank, so appending them blindly gives you
  // "12 Toomey Street, Yarraman QLD 4614 / QLD, 4614". Only add the parts that
  // aren't already in what we're showing. Used for both the display and the
  // directions link so the two can't drift apart.
  const addressLines = (() => {
    const lines: string[] = [];
    if (address) lines.push(String(address));
    const extra = [suburb, state, postcode]
      .filter(Boolean)
      .map(String)
      .filter((p) => !lines.join(" ").toLowerCase().includes(p.toLowerCase()));
    if (extra.length) lines.push(extra.join(", "));
    return lines;
  })();

  // GPS coordinates first, then the real street address. Never the bare
  // suburb/town or the site name — a "Beechmont" destination navigates to the
  // middle of a township, not the compound (James, 9 Sep 2026). No coords and
  // no street address ⇒ no directions link.
  const destination = (lat != null && lng != null)
    ? `${lat},${lng}`
    : (address ? addressLines.join(", ") : null);

  // dir/?api=1 is Google's documented cross-platform directions link — opens the
  // Maps app on a phone, the web app on a desktop.
  const mapsUrl = destination
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    : null;

  const fmtCoord = (n: any) => Number(n).toFixed(5);

  // Everything below is already on the sites row and already arriving here —
  // the project query selects sites(*). It has simply never been drawn on this
  // screen, only on the Site page, which is not what a crew opens on the way
  // to a job.
  //
  // Every block renders ONLY when the field has a value. No "Not recorded"
  // placeholders: as at 17/08/2026, across 62 active sites, key location,
  // parking, access restrictions, hazards, evacuation point, what3words and
  // all four nearest-hospital fields are empty on every single one. A column
  // of "Not recorded" would teach the crew this screen knows nothing and they
  // would stop reading it. Fill a field in on the Site page and it appears
  // here on its own, no code change.
  const structure = [
    site?.site_type ? String(site.site_type) : null,
    site?.structure_height_m != null ? `${site.structure_height_m} m` : null,
    site?.rfnsa_id ? `RFNSA ${site.rfnsa_id}` : null,
  ].filter(Boolean).join("  ·  ");

  // Only two free-text fields are drawn here. The sites table also carries
  // access_restrictions, key_location, parking_notes, hazards_summary,
  // evacuation_point, mobile_coverage and four nearest-hospital columns — that
  // schema was modelled on a principal contractor's site pack. Coretel works
  // as a subcontractor under tier ones and is not given that information, so
  // those columns are empty on all 62 sites and realistically will stay that
  // way. They are still editable on the Site page if a site ever warrants it;
  // they are just not worth a row on the screen a crew reads before a drive.
  const access    = site?.access_details ?? null;
  const siteNotes = site?.notes ?? null;

  const hasDetail = !!(structure || access || siteNotes);

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <SectionHeading icon={MapPin}>Site</SectionHeading>
          {canManage && projectId && !changing && <button type="button" className="text-[11px] text-accent hover:underline" onClick={() => setChanging(true)}>Change site</button>}
        </div>
        {changing && projectId && <ChangeSitePicker projectId={projectId} currentSiteId={site?.site_id ?? null} onClose={() => setChanging(false)} onChanged={() => { setChanging(false); onChanged?.(); }} />}

        {/* Identity */}
        <div className="mt-3">
          <div className="font-mono text-[12px] text-accent tracking-wide">{site.site_code ?? "—"}</div>
          <div className="text-text text-[15px] font-semibold leading-tight mt-0.5">
            {site.site_name ?? ""}
          </div>
          {site.asset_owner?.owner_code && (
            <div className="text-text-muted text-[12px] mt-1">
              Owner: <span className="font-mono">{site.asset_owner.owner_code}</span> · {site.asset_owner.display_name}
            </div>
          )}
        </div>

        {/* Address */}
        {addressLines.length > 0 && (
          <div className="mt-3 text-[13px] text-text-muted leading-relaxed">
            {addressLines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}

        {/* Field facts — coordinates and drive distance, side by side */}
        <div className="mt-4 pt-4 border-t border-line-light grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">Coordinates</div>
            {lat != null && lng != null ? (
              <div className="font-mono text-[12px] text-text">
                {fmtCoord(lat)}, {fmtCoord(lng)}
              </div>
            ) : (
              <div className="text-[12px] text-text-dim italic">Not recorded</div>
            )}
            {w3w && (
              <div className="font-mono text-[11px] text-text-muted mt-1">///{w3w}</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">From Brisbane GPO</div>
            {km != null ? (
              <div className="font-mono text-[12px] text-text"><DistanceFromBne site={{ ...site, latitude: lat, longitude: lng }} canSave/></div>
            ) : (
              <div className="text-[12px] text-text-dim italic">—</div>
            )}
          </div>
        </div>

        {/* Action */}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={lat != null && lng != null
              ? "Directions to the site coordinates"
              : "Directions to the site address"}
            className="mt-4 inline-flex items-center gap-2 rounded
                       px-4 py-2 text-[13px] font-medium tracking-tight
                       bg-info text-white
                       hover:brightness-110 active:scale-[0.99]
                       transition-all duration-150"
          >
            <Navigation size={14}/>
            Navigate to site
            <ExternalLink size={11} className="opacity-70"/>
          </a>
        )}

        {/* What we actually know about this site. Absent fields draw nothing. */}
        {hasDetail && (
          <div className="mt-5 pt-4 border-t border-line-light space-y-4">
            {structure && <SiteFact label="Structure">{structure}</SiteFact>}
            {access && <SiteFact label="Access">{access}</SiteFact>}
            {siteNotes && <SiteFact label="Site notes">{siteNotes}</SiteFact>}
          </div>
        )}
      </div>
    </Card>
  );
}

// Small label-over-value block, matching the Coordinates / From Brisbane GPO
// pair above it so the card reads as one thing. whitespace-pre-line so a
// hazard note typed with line breaks keeps them.
function ChangeSitePicker({ projectId, currentSiteId, onClose, onChanged }: { projectId: string; currentSiteId: string | null; onClose: () => void; onChanged: () => void }) {
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { data: sites = [] } = useQuery({
    queryKey: ["change_site_options", q],
    queryFn: async () => {
      let qq: any = supabase.from("sites").select("site_id, site_code, site_name, suburb, state").order("site_code").limit(30);
      if (q.trim()) qq = qq.or(`site_code.ilike.%${q.trim()}%,site_name.ilike.%${q.trim()}%,suburb.ilike.%${q.trim()}%`);
      const { data, error } = await qq; if (error) throw error; return (data ?? []) as any[];
    },
  });
  const save = useMutation({
    mutationFn: async () => { if (!pick) return; const { error } = await supabase.from("projects").update({ site_id: pick.site_id }).eq("project_id", projectId); if (error) throw error; },
    onSuccess: onChanged, onError: (e: any) => setErr(e?.message ?? String(e)),
  });
  return (
    <div className="mt-3 rounded-md border border-warn/50 bg-warn/[0.06] p-3 text-[12.5px]">
      <div className="font-medium mb-1.5">Move this project to another site</div>
      <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPick(null); }} placeholder="Search by site code, name or suburb…" className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text mb-2"/>
      <div className="max-h-[200px] overflow-y-auto divide-y divide-line border border-line rounded-md bg-ink">
        {sites.filter(x => x.site_id !== currentSiteId).map(x => (
          <button key={x.site_id} type="button" onClick={() => setPick(x)} className={`w-full text-left px-2.5 py-1.5 hover:bg-ink-raised ${pick?.site_id === x.site_id ? "bg-accent/10" : ""}`}>
            <span className="font-mono text-[12px] text-accent">{x.site_code}</span> <span className="text-text">{x.site_name}</span> <span className="text-text-muted">{[x.suburb, x.state].filter(Boolean).join(" ")}</span>
          </button>
        ))}
        {sites.length === 0 && <div className="px-2.5 py-2 text-text-muted">No site matches.</div>}
      </div>
      {pick && <div className="mt-2 text-[12px] text-text-muted">The project moves to <span className="font-mono text-text">{pick.site_code}</span>. Bookings, diary and documents stay with the project. If a site folder was linked from the wrong site, re-link it on the Site folder tab.</div>}
      {err && <div className="mt-2 text-[12px] text-danger">{err}</div>}
      <div className="mt-2 flex gap-2 justify-end">
        <Button size="sm" variant="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button size="sm" disabled={!pick || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Moving…" : "Move the project"}</Button>
      </div>
    </div>
  );
}

function SiteFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">{label}</div>
      <div className="text-[12px] text-text leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
}

// =============================================================================
// DEPRECATED — flat DocumentsTab + DOC_TYPES constants.
// Superseded 2026-05-01 by LibraryDocumentsTab (T-161 hierarchical library).
// Left in place as a fallback during the rollout — once the new library is
// running cleanly in prod we can rip this whole block out.
// =============================================================================
// Documents tab — reads from bid_documents where project_id = this project
// =============================================================================
// DOC_TYPES — refined per James 2026-04-25 to reflect actual project usage.
// Was: design, drawing, scope, rfq, spec, sow, email, photo, other.
// Now: just the categories crews actually upload against. doc_type is a free
// text column on bid_documents (no enum), so we can refine this list freely
// over time as patterns emerge from real project use.
// Stop-gap list — proper hierarchical library is logged as a separate
// ticket. RLM/Acceptance/As-Built added so build-team uploads land cleanly
// and the HOP submission screen can pick them up by type.
const DOC_TYPES = [
  { value: "design",         label: "Design (FC / IFC)",        tone: "bid"     as const },
  { value: "rlm",            label: "RLM (Red Line Markup)",    tone: "bid"     as const },
  { value: "as_built",       label: "As-Built / Final RLM",     tone: "bid"     as const },
  { value: "saed",           label: "SAED",                     tone: "bid"     as const },
  { value: "geo_civil",      label: "Geo / Civil report",       tone: "bid"     as const },
  { value: "sweep_test",     label: "Sweep test (Anritsu)",     tone: "bid"     as const },
  { value: "concrete_test",  label: "Concrete test report",     tone: "bid"     as const },
  { value: "form_16",        label: "Form 16 (Engineering)",    tone: "bid"     as const },
  { value: "permits",        label: "Permits",                  tone: "pending" as const },
  { value: "colocation",     label: "Co-Location",              tone: "pending" as const },
  { value: "email",          label: "Email",                    tone: "pending" as const },
  { value: "other",          label: "Other",                    tone: "pending" as const },
];

function DocumentsTab({ project, canEdit }: { project: any; canEdit: boolean }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState<string>("design");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["project_documents", project.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_documents")
        .select("id, bid_id, project_id, file_name, file_path, file_size_bytes, mime_type, doc_type, title, uploaded_at")
        .eq("project_id", project.project_id)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Two upload paths depending on whether the project came from a BID:
      //   - With source BID → store under {bid_id}/{ts}-{name} so docs from
      //     that BID stay grouped (existing behaviour).
      //   - Without source BID → store under project-{project_id}/{ts}-{name}.
      //     bid_documents.bid_id becomes null; project_id is the link
      //     (mig 101 relaxed the NOT NULL + added check constraint).
      const pathPrefix = project.source_bid_id
        ? project.source_bid_id
        : `project-${project.project_id}`;

      for (const file of Array.from(files)) {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
        const path = `${pathPrefix}/${ts}-${safeName}`;

        const { error: upErr } = await supabase.storage
          .from("bid-documents")
          .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
        if (upErr) throw upErr;

        const { error: insErr } = await supabase.from("bid_documents").insert({
          bid_id:          project.source_bid_id ?? null,
          project_id:      project.project_id,
          file_name:       file.name,
          file_path:       path,
          file_size_bytes: file.size,
          mime_type:       file.type || null,
          doc_type:        uploadType,
        });
        if (insErr) throw insErr;
      }
      qc.invalidateQueries({ queryKey: ["project_documents", project.project_id] });
    } catch (e: any) {
      setUploadError(e?.message ?? String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const deleteMut = useMutation({
    mutationFn: async (doc: any) => {
      const { error: sErr } = await supabase.storage.from("bid-documents").remove([doc.file_path]);
      if (sErr) throw sErr;
      const { error: dErr } = await supabase.from("bid_documents").delete().eq("id", doc.id);
      if (dErr) throw dErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project_documents", project.project_id] }),
  });

  async function handleDownload(doc: any) {
    // James, 8 Sep 2026: docs stopped opening on phones. Mobile browsers only
    // allow a new tab opened SYNCHRONOUSLY in the tap handler — after the
    // await on createSignedUrl() the popup is silently blocked. Open a blank
    // window NOW, navigate it once we have the URL (same pattern as
    // LibraryDocumentsTab.handleDownload).
    // 9 Sep: "noopener" makes window.open return NULL — tab opened but could
    // never be navigated (blank page on phones). No features = handle back;
    // sever opener by hand.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    const { data, error } = await supabase.storage
      .from("bid-documents")
      .createSignedUrl(doc.file_path, 60 * 10);
    if (error || !data?.signedUrl) {
      if (win) win.close();
      alert(error?.message ?? "Could not load document");
      return;
    }
    const mime = doc.mime_type ?? "";
    const inlineable = mime === "application/pdf" || mime.startsWith("image/");
    const url = inlineable
      ? `${data.signedUrl}&response-content-disposition=inline`
      : data.signedUrl;
    if (win) win.location.href = url;
    else window.location.href = url;  // popup blocked entirely — same-tab fallback
  }

  function fmtBytes(n: number | null | undefined): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n/1024).toFixed(0)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between gap-2 mb-3">
            <SectionHeading icon={Paperclip}>
              Project documents {docs.length > 0 && <span className="ml-1 font-mono text-text-muted">({docs.length})</span>}
            </SectionHeading>
            {project.source_bid_id && (
              <Link to={`/bids/${project.source_bid_id}`} className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
                manage via source BID <ExternalLink size={10}/>
              </Link>
            )}
          </div>

          {canEdit && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] text-text-muted">Upload as:</span>
                <Select
                  value={uploadType}
                  onChange={(e: any) => setUploadType(e.target.value)}
                  className="!text-[11px] !py-1 !w-auto"
                >
                  {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </div>
              <label
                className={`block rounded border border-dashed border-line hover:border-accent/40 bg-ink/40 px-4 py-6 text-center cursor-pointer transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFiles(e.dataTransfer.files); }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <Upload size={16} className="text-text-muted mx-auto mb-1.5"/>
                <div className="text-[12px] text-text-muted">
                  {uploading ? "Uploading…" : "Drop files here or click to browse"}
                </div>
                <div className="text-[10px] text-text-dim mt-1">
                  PDF, DWG, images, Word — up to 50 MB each
                </div>
              </label>
              {uploadError && (
                <div className="mt-2 bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
                  <AlertTriangle size={11} className="inline mr-1"/>
                  {uploadError}
                </div>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="py-3 flex justify-center"><Spinner/></div>
          ) : docs.length === 0 ? (
            <div className="py-6 text-center">
              <FileText size={22} className="text-text-dim mx-auto mb-2"/>
              <div className="text-[12px] text-text-muted">No documents on this project yet.</div>
              {project.source_bid_id && (
                <div className="text-[11px] text-text-dim mt-1">
                  Upload above, or via the <Link to={`/bids/${project.source_bid_id}`} className="text-accent">source BID</Link>.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {docs.map((d) => {
                const typeMeta = DOC_TYPES.find(t => t.value === d.doc_type) ?? DOC_TYPES[DOC_TYPES.length - 1];
                return (
                  <div key={d.id} className="px-3 py-2 rounded border border-line hover:bg-ink-hover transition-colors flex items-center gap-3">
                    <Pill label={typeMeta.label} tone={typeMeta.tone} small/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-text truncate">{d.file_name}</div>
                      <div className="text-[10px] font-mono text-text-muted">
                        {fmtBytes(d.file_size_bytes)} · uploaded {fmtDate(d.uploaded_at)}
                        {d.bid_id && d.project_id && <span className="ml-2 text-text-dim">· carried from BID</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(d)}
                      className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-text"
                      title="Download"
                    >
                      <Download size={14}/>
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => { if (confirm(`Delete ${d.file_name}?`)) deleteMut.mutate(d); }}
                        className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-danger"
                        title="Delete"
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {project.sharepoint_folder_url && (
        <a href={project.sharepoint_folder_url} target="_blank" rel="noreferrer" className="inline-block">
          <Button icon={ExternalLink} variant="secondary">Open SharePoint folder (legacy)</Button>
        </a>
      )}
    </div>
  );
}

// =============================================================================
// Financials tab — KPIs + source quote link + budget vs spend
// =============================================================================
function FinancialsTab({ project }: { project: any }) {
  const navigate = useNavigate();
  const user = useRequireAuth();   // 6 Oct 2026 — who a freshly made quote PDF says prepared it

  // "$-8,941.24" is how a computer writes an overrun; "-$8,941.24" is how a
  // person reads one. Used for every balance/remaining figure on this tab.
  const fmtBal = (n: number) => (n < 0 ? "-" : "") + String(fmtMoney(Math.abs(n)));
  // Money columns read better when zero is quiet.
  const fmtOrDash = (n: number) => (Number(n) === 0 ? "—" : fmtMoney(n));

  // Source quote
  const { data: sourceQuote } = useQuery({
    queryKey: ["project_source_quote", project.source_quote_id],
    enabled: !!project.source_quote_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("quote_id, quote_number, subtotal_excl_gst, total_incl_gst, margin_percent, accepted_at, pdf_storage_path, pdf_generated_at")
        .eq("quote_id", project.source_quote_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // T-233 — Variations on this project (any quote with quote_type='variation'
  // pointing at the source quote OR with project_id = this project). Listed
  // under Source Documents alongside the base quote so manager sees how the
  // contract value rolled up.
  const { data: variations = [] } = useQuery({
    queryKey: ["project_variations", project.project_id],
    enabled: !!project.project_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("quote_id, quote_number, quote_title, quote_type, status, subtotal_excl_gst, accepted_at, pdf_storage_path, pdf_generated_at")
        .eq("project_id", project.project_id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      // 9 Oct 2026 (mig 375/439) — a project can carry more than one STANDARD
      // quote (award first, quote later; a standalone quote won onto it). They
      // add to contract value exactly like a variation, so they are listed and
      // counted with them. Variations: every status, as before. Extra standard
      // quotes: only the ones that are live paper (not the source quote, not
      // drafts or replaced revisions).
      return ((data ?? []) as any[]).filter(q =>
        q.quote_type === "variation"
        || (q.quote_id !== project.source_quote_id
            && !["draft", "superseded", "withdrawn", "expired"].includes(q.status)));
    },
  });

  // Budget lines (new UUID-based table from hotfix_024)
  const { data: budgetLines = [], isLoading: blLoading } = useQuery({
    queryKey: ["project_budget_lines_v2", project.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("budget_lines")
        .select("budget_line_id, budget_code, category, description, planned_amount_ex_gst, quoted_sell_ex_gst, sort_order")
        .eq("project_id", project.project_id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Budget spend (new UUID-based table from hotfix_024)
  const { data: spend = [] } = useQuery({
    queryKey: ["project_budget_spend_v2", project.project_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("budget_spend")
        .select("spend_id, budget_line_id, spend_type, amount_ex_gst, spend_date, reference_no, description, status, supplier_name_cache, source_record_type, source_record_id")
        .eq("project_id", project.project_id)
        .order("spend_date", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // 28 Aug 2026 - James, looking at live Eton: "the spend invoices just have
  // dates, and franna charge on one line but its a full invoice". The sync
  // triggers don't cache supplier/invoice-number onto budget_spend, so look
  // them up in batches from the source records (same pattern as the Excel
  // export): AP invoices -> supplier + invoice no, POs -> PO number,
  // timesheets -> who + hours/nights.
  const spendSourceIds = React.useMemo(() => ({
    ap: [...new Set(spend.filter((s: any) => s.source_record_type === "ap_invoice" && s.source_record_id).map((s: any) => String(s.source_record_id)))],
    po: [...new Set(spend.filter((s: any) => s.source_record_type === "purchase_order" && s.source_record_id).map((s: any) => String(s.source_record_id)))],
    ts: [...new Set(spend.filter((s: any) => s.source_record_type === "timesheet_entry" && s.source_record_id).map((s: any) => String(s.source_record_id)))],
  }), [spend]);

  const { data: spendMeta } = useQuery({
    queryKey: ["project_spend_meta", project.project_id, spendSourceIds.ap.join(","), spendSourceIds.po.join(","), spendSourceIds.ts.join(",")],
    enabled: spendSourceIds.ap.length + spendSourceIds.po.length + spendSourceIds.ts.length > 0,
    queryFn: async (): Promise<SpendMeta> => {
      const meta: SpendMeta = { ap: new Map(), po: new Map(), ts: new Map() };
      if (spendSourceIds.ap.length) {
        const { data } = await supabase.from("ap_invoices")
          .select("ap_invoice_id, supplier_name, invoice_number")
          .in("ap_invoice_id", spendSourceIds.ap);
        for (const a of (data ?? []) as any[]) meta.ap.set(String(a.ap_invoice_id), a);
      }
      if (spendSourceIds.po.length) {
        const { data } = await supabase.from("purchase_orders")
          .select("po_id, po_number, suppliers:supplier_id ( legal_name )")
          .in("po_id", spendSourceIds.po);
        for (const p of (data ?? []) as any[]) meta.po.set(String(p.po_id), p);
      }
      // employees embed must be disambiguated (employees:employee_id) - the
      // mirrored_from_employee_id FK makes a bare employees(...) ambiguous.
      for (let i = 0; i < spendSourceIds.ts.length; i += 200) {
        const { data } = await supabase.from("timesheet_entries")
          .select("entry_id, total_hours, lafha_nights, employees:employee_id ( first_name, last_name )")
          .in("entry_id", spendSourceIds.ts.slice(i, i + 200));
        for (const t of (data ?? []) as any[]) meta.ts.set(String(t.entry_id), t);
      }
      return meta;
    },
  });

  // Past client claims — read from client_invoices (T-229). Maps to the same
  // shape the Financials tab UI expects (invoice_amount = total_incl_gst,
  // invoice_date_sent = invoice_date, invoice_status = status, paid_date = paid_at,
  // amount_paid + balance_outstanding derived from status).
  const { data: pastClaims = [] } = useQuery({
    queryKey: ["project_past_claims_fin", project.project_id],
    enabled: !!project.project_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("client_invoices")
        .select("invoice_id, invoice_number, total_incl_gst, status, invoice_date, paid_at")
        .eq("project_id", project.project_id)
        .order("invoice_date", { ascending: false });
      return (data ?? []).map((row: any) => ({
        id:                  row.invoice_id,
        invoice_number:      row.invoice_number,
        invoice_amount:      row.total_incl_gst,
        invoice_date_sent:   row.invoice_date,
        invoice_status:      row.status,
        paid_date:           row.paid_at,
        amount_paid:         row.status === "paid" ? row.total_incl_gst : 0,
        balance_outstanding: row.status === "paid" ? 0 : row.total_incl_gst,
      })) as any[];
    },
  });

  // ---------------------------------------------------------------------------
  // Contracted quote lines across the base quote + WON variations. Feeds two
  // things: the crew-days tracker (labour hours quoted) and the component
  // statement generator (plant/subcontract lines at quoted amounts).
  // ---------------------------------------------------------------------------
  const wonQuoteIds = React.useMemo(() => {
    const ids: string[] = [];
    if (project.source_quote_id) ids.push(project.source_quote_id);
    for (const v of variations) {
      if (["accepted", "partially_invoiced", "invoiced"].includes(v.status)) ids.push(v.quote_id);
    }
    return ids;
  }, [project.source_quote_id, variations]);

  const quoteNoById = React.useMemo(() => {
    const mp = new Map<string, string>();
    if (sourceQuote) mp.set(sourceQuote.quote_id, sourceQuote.quote_number);
    for (const v of variations) mp.set(v.quote_id, v.quote_number);
    return mp;
  }, [sourceQuote, variations]);

  const { data: wonLines = [] } = useQuery({
    queryKey: ["project_won_quote_lines", project.project_id, wonQuoteIds.join(",")],
    enabled: wonQuoteIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_lines")
        .select("quote_line_id, quote_id, description, description_secondary, unit, quantity, sell_price_ex_gst, line_total_ex_gst, category, is_optional, qty_people, qty_hours, qty_days, rate_line:rate_line_id (line_code)")
        .in("quote_id", wonQuoteIds);
      if (error) throw error;
      return (data ?? []).filter((l: any) => !l.is_optional) as any[];
    },
  });

  // Timesheet hours booked to this project — every entry counts the moment it
  // goes in (that is the point: watching the quoted labour get eaten live).
  const { data: tsRows = [] } = useQuery({
    queryKey: ["project_timesheet_hours", project.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select("total_hours, work_date, lafha_nights")
        .eq("project_id", project.project_id);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Crew-day length is a preference, not a fact - 10hr site days are the norm
  // here, but it's one click to see it as 8s or 12s.
  const [dayHours, setDayHours] = useState<number>(() => {
    try { return Number(window.localStorage.getItem("ct_crewday_hours")) || 10; } catch { return 10; }
  });
  const setDayHoursPersist = (n: number) => {
    setDayHours(n);
    try { window.localStorage.setItem("ct_crewday_hours", String(n)); } catch { /* fine */ }
  };

  const quotedLabourHours = wonLines
    .filter((l: any) => (l.category ?? "").toLowerCase() === "labour" && l.unit === "hour")
    .reduce((a: number, l: any) => a + Number(l.quantity ?? 0), 0);
  const usedHours = tsRows.reduce((a: number, r: any) => a + Number(r.total_hours ?? 0), 0);
  const quotedDays = quotedLabourHours / dayHours;
  const usedDays   = usedHours / dayHours;
  const daysLeft   = quotedDays - usedDays;
  // James's thresholds: green with a real buffer, amber when it's tight, red
  // when the quoted labour is gone. (Interpreted as: >5 days left / 0-5 / <0.)
  const daysTone   = daysLeft > 5 ? "ok" : daysLeft >= 0 ? "warn" : "danger";

  // 28 Aug 2026 - James: "do the same with LAFHA as nights - total in the
  // quote, how many left total and for a crew." Quoted nights come off the
  // won quotes (lafha lines - the quantity IS the person-nights sold); used
  // nights are lafha_nights off every timesheet entry, live. Crew size turns
  // the person-night balance into "how many more nights can the crew stay".
  const quotedNights = wonLines
    .filter((l: any) => (l.category ?? "").toLowerCase() === "lafha" || l.unit === "night")
    .reduce((a: number, l: any) => a + Number(l.quantity ?? 0), 0);
  const usedNights = tsRows.reduce((a: number, r: any) => a + Number(r.lafha_nights ?? 0), 0);
  const nightsLeft = quotedNights - usedNights;
  const nightsTone = nightsLeft > 5 ? "ok" : nightsLeft >= 0 ? "warn" : "danger";
  const [crewSize, setCrewSize] = useState<number>(() => {
    try { return Number(window.localStorage.getItem("ct_crew_size")) || 3; } catch { return 3; }
  });
  const setCrewSizePersist = (n: number) => {
    setCrewSize(n);
    try { window.localStorage.setItem("ct_crew_size", String(n)); } catch { /* fine */ }
  };

  // Component statement dialog
  const [stmtOpen, setStmtOpen] = useState(false);
  // Broker SOW dialog — T-INS-SOW
  const [sowOpen, setSowOpen] = useState(false);

  const contract = Number(project.contract_value ?? 0);
  const invoiced = Number(project.invoiced_to_date ?? 0);
  const paid     = Number(project.paid_to_date ?? 0);

  const plannedTotal   = budgetLines.reduce((a, b) => a + Number(b.planned_amount_ex_gst ?? 0), 0);
  const quotedSellTotal = budgetLines.reduce((a, b) => a + Number(b.quoted_sell_ex_gst ?? 0), 0);
  const spendByLine: Record<string, { committed: number; actual: number }> = {};
  for (const s of spend) {
    const key = s.budget_line_id ?? "__unassigned__";
    spendByLine[key] = spendByLine[key] || { committed: 0, actual: 0 };
    if (s.spend_type === "committed") spendByLine[key].committed += Number(s.amount_ex_gst ?? 0);
    else                              spendByLine[key].actual    += Number(s.amount_ex_gst ?? 0);
  }

  // Group budget lines by budget_code. Non-null codes roll up into a single
  // parent row with summed totals and an expandable breakdown; uncoded lines
  // render as flat single-line groups (no chevron).
  type BudgetGroup = {
    key: string;
    budget_code: string | null;
    lines: any[];
    planned: number;
    committed: number;
    actual: number;
    firstSort: number;
  };
  const groupMap = new Map<string, BudgetGroup>();
  for (const b of budgetLines) {
    const groupKey = b.budget_code ? `code:${b.budget_code}` : `line:${b.budget_line_id}`;
    let g = groupMap.get(groupKey);
    if (!g) {
      g = {
        key: groupKey,
        budget_code: b.budget_code ?? null,
        lines: [],
        planned: 0,
        committed: 0,
        actual: 0,
        firstSort: Number(b.sort_order ?? 0),
      };
      groupMap.set(groupKey, g);
    }
    const sp = spendByLine[b.budget_line_id] || { committed: 0, actual: 0 };
    g.lines.push(b);
    g.planned   += Number(b.planned_amount_ex_gst ?? 0);
    g.committed += sp.committed;
    g.actual    += sp.actual;
    g.firstSort = Math.min(g.firstSort, Number(b.sort_order ?? 0));
  }
  const groups = Array.from(groupMap.values()).sort((a, b) => a.firstSort - b.firstSort);

  // 28 Aug 2026 - James: "include the AP receipts coded to that project and
  // code, to see not just the spend but the invoices paid on that code."
  // Every group carries its actual spend entries (the receipts/bills behind
  // the number), shown when the row is expanded. Spend that reached the
  // project without landing on any budget line gets its own Unallocated row
  // instead of silently vanishing from the table.
  const lineIdsByGroup = new Map<string, Set<string>>();
  for (const g of groups) {
    lineIdsByGroup.set(g.key, new Set(g.lines.map((b: any) => b.budget_line_id)));
  }
  const spendRowsByGroup = new Map<string, any[]>();
  const knownLineIds = new Set(budgetLines.map((b: any) => b.budget_line_id));
  const unallocated: any[] = [];
  for (const sRow of spend) {
    if (sRow.budget_line_id && knownLineIds.has(sRow.budget_line_id)) {
      for (const g of groups) {
        if (lineIdsByGroup.get(g.key)!.has(sRow.budget_line_id)) {
          if (!spendRowsByGroup.has(g.key)) spendRowsByGroup.set(g.key, []);
          spendRowsByGroup.get(g.key)!.push(sRow);
          break;
        }
      }
    } else {
      unallocated.push(sRow);
    }
  }
  const unallocatedTotal = {
    committed: unallocated.filter(x => x.spend_type === "committed").reduce((a, x) => a + Number(x.amount_ex_gst ?? 0), 0),
    actual:    unallocated.filter(x => x.spend_type !== "committed").reduce((a, x) => a + Number(x.amount_ex_gst ?? 0), 0),
  };
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const toggleGroup = (key: string) => {
    setExpandedCodes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const totalCommitted = spend.filter(s => s.spend_type === "committed").reduce((a, s) => a + Number(s.amount_ex_gst ?? 0), 0);
  const totalActual    = spend.filter(s => s.spend_type !== "committed").reduce((a, s) => a + Number(s.amount_ex_gst ?? 0), 0);
  const totalSpendSoFar = totalCommitted + totalActual;
  const forecastMargin = contract - totalSpendSoFar;
  const forecastMarginPct = contract > 0 ? (forecastMargin / contract) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 mb-1">
        <Button icon={Download} variant="secondary" size="sm" disabled={exporting}
          onClick={async () => { setExporting(true); try { await exportProjectFinancialsXlsx(project.project_id); } catch (e: any) { alert(e?.message ?? "Export failed"); } finally { setExporting(false); } }}>
          {exporting ? "Exporting…" : "Export (Excel)"}
        </Button>
        <Button icon={FileText} variant="secondary" size="sm" disabled={exporting}
          onClick={async () => { setExporting(true); try { await exportProjectFinancialsPdf(project.project_id); } catch (e: any) { alert(e?.message ?? "Export failed"); } finally { setExporting(false); } }}>
          PDF report
        </Button>
        <Button icon={FileText} variant="secondary" size="sm" onClick={() => setStmtOpen(true)}
          title="Client-evidence PDF of the quoted plant & subcontract lines - references a real claim invoice, never touches Xero">
          Component statement
        </Button>
        <Button icon={FileText} variant="secondary" size="sm" onClick={() => setSowOpen(true)}
          title="One-page site scope-of-works summary for the insurance broker — drafted from live project data, reviewed by you, forwarded manually (T-INS-SOW)">
          Broker SOW
        </Button>
        <Link to={`/quotes/new?project=${project.project_id}&mode=variation`}>
          <Button icon={FileText} variant="secondary" size="sm" title="A variation on this job's accepted quote — the project, client and site are already set">Variation</Button>
        </Link>
        <Link to={`/projects/${project.project_id}/invoice/new`}>
          <Button icon={DollarSign} variant="secondary" size="sm">Issue invoice</Button>
        </Link>
      </div>

      {/* All five tiles are EX-GST so they're directly comparable.
          23 Oct 2026 (mig 449): the trigger had been summing total_incl_gst
          since mig 135, so Invoiced sat 10% above the contract on a fully
          claimed job (Eton showed $128,855 against $117,141). Now ex GST.
          Outstanding is what's owed on invoices (invoiced − paid); Still to
          claim is work done with no invoice yet (contract − invoiced). */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Contract value (ex GST)" value={fmtMoney(contract)} />
        <StatCard label="Invoiced (ex GST)"       value={fmtMoney(invoiced)} />
        <StatCard label="Paid (ex GST)"           value={fmtMoney(paid)} />
        <StatCard label="Outstanding on invoices" value={fmtMoney(invoiced - paid)} />
        <StatCard label={contract - invoiced < -0.005 ? "Over-claimed (ex GST)" : "Still to claim (ex GST)"} value={fmtMoney(Math.abs(contract - invoiced))} />
      </div>

      {/* Live margin */}
      {budgetLines.length > 0 && (
        <Card>
          <div className="p-5">
            <SectionHeading icon={DollarSign}>Project margin (live)</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
              <Kv label="Contract (client)" value={fmtMoney(contract)} mono/>
              <Kv label="Planned cost"      value={fmtMoney(plannedTotal)} mono/>
              <Kv label="Committed + actual" value={fmtMoney(totalSpendSoFar)} mono/>
              <Kv label="Forecast margin" value={
                <span className={forecastMargin < 0 ? "text-danger" : forecastMarginPct >= 15 ? "text-ok" : "text-warn"}>
                  {fmtMoney(forecastMargin)} · {forecastMarginPct.toFixed(0)}%
                </span>
              } mono/>
            </div>
          </div>
        </Card>
      )}

      {/* ---- Crew days - the quoted labour clock (28 Aug 2026) ------------
          James: "I want to see the quoted days estimate for the team and then
          as timesheets go in, a clean card with the balance." Quoted labour
          hours come off the won quotes (labour-category hour lines); used
          hours are every timesheet entry booked to this project, live. */}
      {(quotedLabourHours > 0 || quotedNights > 0 || usedNights > 0) && (
        <Card>
          <div className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <SectionHeading icon={Clock}>Crew days - quoted vs booked</SectionHeading>
              {/* 15 Sep 2026 — James: "Days is nice here but we need a crew size
                  as well to accurately see the days allocated." Hours ÷ day
                  length = person-days; ÷ crew = the site days he plans with. */}
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted">
                  day =
                  <select value={dayHours} onChange={e => setDayHoursPersist(Number(e.target.value))}
                    className="bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text">
                    <option value={8}>8 hrs</option>
                    <option value={10}>10 hrs</option>
                    <option value={12}>12 hrs</option>
                  </select>
                </label>
                <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted">
                  crew of
                  <select value={crewSize} onChange={e => setCrewSizePersist(Number(e.target.value))}
                    className="bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text">
                    {[1, 2, 3, 4, 5, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
            </div>
            {quotedLabourHours > 0 && (<>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Quoted</div>
                <div className="text-[20px] font-semibold font-mono mt-0.5">{(quotedDays / crewSize).toFixed(1)}<span className="text-[12px] text-text-muted font-normal"> site days</span></div>
                <div className="text-[10px] font-mono text-text-dim">{quotedDays.toFixed(1)} person-days · {quotedLabourHours.toFixed(0)} hrs sold</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Booked on timesheets</div>
                <div className="text-[20px] font-semibold font-mono mt-0.5">{(usedDays / crewSize).toFixed(1)}<span className="text-[12px] text-text-muted font-normal"> site days</span></div>
                <div className="text-[10px] font-mono text-text-dim">{usedDays.toFixed(1)} person-days · {usedHours.toFixed(0)} hrs · {tsRows.length} entries</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Balance</div>
                <div className={`text-[20px] font-semibold font-mono mt-0.5 ${
                  daysTone === "ok" ? "text-ok" : daysTone === "warn" ? "text-warn" : "text-danger"}`}>
                  {daysLeft >= 0 ? (daysLeft / crewSize).toFixed(1) : `-${(Math.abs(daysLeft) / crewSize).toFixed(1)}`}<span className="text-[12px] font-normal"> site days</span>
                </div>
                <div className="text-[10px] font-mono text-text-dim">{daysLeft.toFixed(1)} person-days for a crew of {crewSize}</div>
                <div className="text-[10px] text-text-dim">
                  {daysTone === "ok" ? "buffer in hand" : daysTone === "warn" ? "getting tight - watch it" : "labour sold is spent - margin now eroding"}
                </div>
              </div>
            </div>
            {/* One bar: booked over quoted, red overflow past 100%. */}
            <div className="mt-3 bg-ink-raised rounded h-2.5 overflow-hidden relative">
              <div className={`h-full ${daysTone === "danger" ? "bg-danger" : daysTone === "warn" ? "bg-warn" : "bg-ok"}`}
                style={{ width: `${Math.min((usedDays / Math.max(quotedDays, 0.01)) * 100, 100)}%` }} />
            </div>
            <div className="mt-1 text-[10px] font-mono text-text-dim text-right">
              {quotedDays > 0 ? ((usedDays / quotedDays) * 100).toFixed(0) : 0}% of quoted labour used
            </div>
            </>)}

            {/* ---- Nights away (TA / LAFHA) - same clock, one row down ----
                James: "do the same with LAFHA as nights - total in quote,
                how many left total and for a crew." Nights sold come off
                the quote's LAFHA lines; used nights are timesheet
                lafha_nights. The crew selector answers his real question:
                how many more nights can the whole crew stay away. */}
            {(quotedNights > 0 || usedNights > 0) && (
              <div className={quotedLabourHours > 0 ? "mt-4 pt-4 border-t border-line/60" : "mt-3"}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">
                    Nights away (TA / LAFHA)
                  </div>
                  <label className="inline-flex items-center gap-1.5 text-[11px] text-text-muted">
                    crew of
                    <select value={crewSize} onChange={e => setCrewSizePersist(Number(e.target.value))}
                      className="bg-ink border border-line rounded px-1.5 py-0.5 text-[11px] text-text">
                      {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted">Quoted</div>
                    <div className="text-[20px] font-semibold font-mono mt-0.5">{quotedNights.toFixed(0)}<span className="text-[12px] text-text-muted font-normal"> nights</span></div>
                    <div className="text-[10px] font-mono text-text-dim">person-nights in the quote</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted">Used on timesheets</div>
                    <div className="text-[20px] font-semibold font-mono mt-0.5">{usedNights.toFixed(0)}<span className="text-[12px] text-text-muted font-normal"> nights</span></div>
                    <div className="text-[10px] font-mono text-text-dim">off timesheet TA entries</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted">Balance</div>
                    <div className={`text-[20px] font-semibold font-mono mt-0.5 ${
                      nightsTone === "ok" ? "text-ok" : nightsTone === "warn" ? "text-warn" : "text-danger"}`}>
                      {nightsLeft >= 0 ? nightsLeft.toFixed(0) : `-${Math.abs(nightsLeft).toFixed(0)}`}<span className="text-[12px] font-normal"> nights</span>
                    </div>
                    <div className="text-[10px] text-text-dim">
                      {nightsLeft > 0
                        ? `≈ ${Math.floor(nightsLeft / crewSize)} more night${Math.floor(nightsLeft / crewSize) === 1 ? "" : "s"} away for a crew of ${crewSize}`
                        : nightsTone === "warn" ? "TA sold is fully used" : "over the TA sold - eating margin"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 bg-ink-raised rounded h-2.5 overflow-hidden relative">
                  <div className={`h-full ${nightsTone === "danger" ? "bg-danger" : nightsTone === "warn" ? "bg-warn" : "bg-ok"}`}
                    style={{ width: `${Math.min((usedNights / Math.max(quotedNights, 0.01)) * 100, 100)}%` }} />
                </div>
                <div className="mt-1 text-[10px] font-mono text-text-dim text-right">
                  {quotedNights > 0 ? ((usedNights / quotedNights) * 100).toFixed(0) : 0}% of quoted nights used
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Source documents */}
      {sourceQuote && (
        <Card>
          <div className="p-5">
            <SectionHeading icon={FileText}>Source documents</SectionHeading>
            <div className="grid grid-cols-1 gap-4 mt-3">
              {sourceQuote && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">Accepted quote</div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <Link to={`/quotes/${sourceQuote.quote_id}`} className="inline-flex items-center gap-2 hover:opacity-80">
                      <span className="font-mono text-[13px] text-accent">{sourceQuote.quote_number}</span>
                      <ExternalLink size={11} className="text-text-muted"/>
                    </Link>
                    {/* 6 Oct 2026 — the sent copy, one click, no detour through Finance */}
                    <QuotePdfButton quoteId={sourceQuote.quote_id} hasPdf={!!(sourceQuote as any).pdf_storage_path}
                      preparedBy={{ name: [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Coretel Group", email: (user as any)?.email ?? (user as any)?.login_email ?? "" }}/>
                    {(sourceQuote as any).pdf_generated_at && <span className="text-[10px] font-mono text-text-dim">PDF made {fmtDate((sourceQuote as any).pdf_generated_at)}</span>}
                  </div>
                  <div className="text-[12px] font-mono text-text-muted mt-1">
                    {fmtMoney(sourceQuote.subtotal_excl_gst)} ex GST
                    {sourceQuote.margin_percent != null && ` · ${Number(sourceQuote.margin_percent).toFixed(0)}% margin`}
                  </div>
                  {sourceQuote.accepted_at && (
                    <div className="text-[11px] font-mono text-text-dim mt-0.5">
                      accepted {fmtDate(sourceQuote.accepted_at)}
                    </div>
                  )}

                  {/* T-233 — Variations rolled up under the accepted quote */}
                  {variations.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-line">
                      <div className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5">
                        {variations.some(v => v.quote_type !== "variation") ? "Variations & extra quotes" : "Variations"} ({variations.length})
                      </div>
                      <div className="space-y-1.5">
                        {variations.map(v => (
                          <div key={v.quote_id} className="flex items-center gap-2 text-[12px]">
                          <Link
                            to={`/quotes/${v.quote_id}`}
                            className="flex items-center gap-2 text-[12px] hover:opacity-80 flex-1 min-w-0"
                          >
                            <span className="font-mono text-accent text-[11px]">{v.quote_number}</span>
                            {v.quote_type !== "variation" && <span className="text-[9px] uppercase font-mono px-1.5 rounded border bg-ink-raised text-text-muted border-line" title="A separate quote accepted onto this project — not a variation of the base quote">extra quote</span>}
                            <span className="text-text-muted truncate flex-1">{v.quote_title}</span>
                            <span className={`text-[9px] uppercase font-mono px-1.5 rounded border ${
                              v.status === "accepted" ? "bg-ok/10 text-ok border-ok/30" :
                              v.status === "sent"     ? "bg-accent/10 text-accent border-accent/30" :
                              v.status === "rejected" ? "bg-danger/10 text-danger border-danger/30" :
                                                         "bg-ink-raised text-text-muted border-line"
                            }`}>{v.status}</span>
                            <span className="font-mono text-text-muted text-[11px] w-20 text-right">
                              {fmtMoney(v.subtotal_excl_gst)}
                            </span>
                          </Link>
                          <QuotePdfButton small quoteId={v.quote_id} hasPdf={!!v.pdf_storage_path}
                            preparedBy={{ name: [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Coretel Group", email: (user as any)?.email ?? (user as any)?.login_email ?? "" }}/>
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-line text-[11px] font-mono">
                          <span className="text-text-muted">
                            {variations.some(v => v.quote_type !== "variation") ? "Total (base + approved variations & extra quotes)" : "Total (base + approved variations)"}
                          </span>
                          <span className="text-text">
                            {fmtMoney(
                              Number(sourceQuote.subtotal_excl_gst ?? 0) +
                              variations
                                // Include any status that represents a "won" variation —
                                // accepted, partially_invoiced, or invoiced. All three
                                // contributed to the project's contract value.
                                .filter(v => ["accepted", "partially_invoiced", "invoiced"].includes(v.status))
                                .reduce((a, v) => a + Number(v.subtotal_excl_gst ?? 0), 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* 27 Oct 2026 — the STOW / PO register: every order the client has
          sent against this job, which quote each covers, contract vs cover,
          and for an RCTI client the claim steps. The single "Client PO"
          column that used to sit here was one number; a Ventia job has a
          STOW per deliverable. Header PO fields flow in by trigger (462). */}
      <OrderRegisterCard projectId={project.project_id}/>

      {/* 28 Oct 2026 (mig 470/471) — the manager's budget replaces the
          "Budget vs spend" table: original vs current per code, committed /
          actual / remaining / forecast, move money on the record, drill-down
          to the POs, bills and quote lines, alerts. The quote lines are still
          the detail under each code. */}
      <Card>
        <div className="p-4 flex items-center gap-3 flex-wrap text-[12.5px]">
          <Wallet size={15} className="text-accent"/>
          <span>The job's cost budget — what's committed on POs, what's spent, what's left per code — lives on the <span className="font-medium">Budget</span> tab.</span>
          <Link to={`/projects/${project.project_id}?tab=budget`} className="ml-auto text-accent hover:underline inline-flex items-center gap-1">Open the budget <ExternalLink size={11}/></Link>
        </div>
      </Card>

      {sowOpen && (
        <BrokerSowDialog
          project={project}
          lines={wonLines}
          onClose={() => setSowOpen(false)}
        />
      )}

      {stmtOpen && (
        <ComponentStatementDialog
          project={project}
          lines={wonLines}
          quoteNoById={quoteNoById}
          claims={pastClaims}
          onClose={() => setStmtOpen(false)}
        />
      )}

      {/* Past client invoices */}
      {pastClaims.length > 0 && (
        <Card>
          <div className="p-5">
            <SectionHeading icon={FileText}>Client invoices issued</SectionHeading>
            <div className="mt-3 space-y-1.5">
              {pastClaims.map(c => (
                <div key={c.id} className="flex items-center gap-3 px-3 py-2 border border-line rounded">
                  <span className="font-mono text-[11px] text-accent">{c.invoice_number}</span>
                  <Pill label={c.invoice_status ?? "—"} tone={c.invoice_status === "Paid" ? "won" : c.invoice_status === "Sent" ? "mobilising" : "pending"} small/>
                  <span className="flex-1 text-[11px] font-mono text-text-muted">{fmtDate(c.invoice_date_sent)}</span>
                  <span className="font-mono text-[12px]">{fmtMoney(c.invoice_amount)}</span>
                  <span className="font-mono text-[11px] text-ok">paid {fmtMoney(c.amount_paid)}</span>
                  <span className={`font-mono text-[11px] ${Number(c.balance_outstanding ?? 0) > 0 ? "text-warn" : "text-text-dim"}`}>
                    ${Number(c.balance_outstanding ?? 0).toFixed(2)} o/s
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// SpendDetailRows — the invoices & receipts behind one budget code (28 Aug).
// =============================================================================
// James: "see not just the spend but the invoices paid on that code" - and,
// from live Eton: "the spend invoices just have dates, and franna charge on
// one line but its a full invoice". Rendered inside the Budget vs spend table
// when a code is expanded: one row per budget_spend entry - date, WHO (the
// supplier off the AP invoice, or the employee off the timesheet), WHICH
// document (invoice no / PO no), what kind of record it is, and the amount in
// the committed (PO) or actual column.
// =============================================================================
type SpendMeta = { ap: Map<string, any>; po: Map<string, any>; ts: Map<string, any> };

const SPEND_SOURCE_LABELS: Record<string, string> = {
  ap_invoice: "AP invoice",
  purchase_order: "PO",
  timesheet_entry: "Timesheet",
};

function spendRowDisplay(sp: any, meta: SpendMeta | undefined) {
  let who = sp.supplier_name_cache || "";
  let ref = sp.reference_no || "";
  let detail = sp.description || "";
  if (sp.source_record_type === "ap_invoice") {
    const a = meta?.ap.get(String(sp.source_record_id));
    who = a?.supplier_name || who || "AP invoice";
    ref = a?.invoice_number || ref;
  } else if (sp.source_record_type === "purchase_order") {
    const p = meta?.po.get(String(sp.source_record_id));
    who = who || p?.suppliers?.legal_name || "";
    ref = p?.po_number || ref;
  } else if (sp.source_record_type === "timesheet_entry") {
    const t = meta?.ts.get(String(sp.source_record_id));
    const name = [t?.employees?.first_name, t?.employees?.last_name].filter(Boolean).join(" ");
    if (name) who = name;
    const isTravel = /travel allowance/i.test(sp.description ?? "");
    if (t && isTravel && Number(t.lafha_nights) > 0) detail = `${Number(t.lafha_nights)} night${Number(t.lafha_nights) === 1 ? "" : "s"} TA`;
    else if (t && Number(t.total_hours) > 0) detail = `${Number(t.total_hours)}h labour`;
  }
  const source = SPEND_SOURCE_LABELS[sp.source_record_type ?? ""] ?? "Manual";
  return { who, ref, detail, source };
}

function SpendDetailRows({ rows, meta }: { rows: any[]; meta?: SpendMeta }) {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => String(b.spend_date ?? "").localeCompare(String(a.spend_date ?? "")));
  return (
    <>
      <tr className="bg-ink-raised/40">
        <td></td>
        <td colSpan={6} className="px-2 py-1 text-[9px] uppercase tracking-wider2 text-text-dim">
          Invoices & receipts on this code
        </td>
      </tr>
      {sorted.map(sp => {
        const d = spendRowDisplay(sp, meta);
        return (
        <tr key={sp.spend_id} className="border-b border-line/40 bg-ink-raised/20">
          <td></td>
          <td className="px-2 py-1 pl-6" colSpan={2}>
            <span className="text-[11px] text-text-muted">
              <span className="font-mono text-text-dim">{fmtDate(sp.spend_date)}</span>
              {d.who && <span className="text-text"> · {d.who}</span>}
              {d.ref && <span className="font-mono text-accent"> · {d.ref}</span>}
              {d.detail && <span className="text-text-dim"> · {d.detail}</span>}
              <span className="ml-2 inline-block align-middle rounded border border-line/70 px-1 py-[1px] text-[9px] uppercase tracking-wider2 text-text-dim">
                {d.source}
              </span>
            </span>
          </td>
          <td className="px-2 py-1 text-right font-mono text-[11px] whitespace-nowrap">
            {sp.spend_type === "committed" ? (
              <span className="text-warn" title="PO raised - not yet invoiced">{fmtMoney(sp.amount_ex_gst)}</span>
            ) : ""}
          </td>
          <td className="px-2 py-1 text-right font-mono text-[11px] whitespace-nowrap">
            {sp.spend_type !== "committed" ? fmtMoney(sp.amount_ex_gst) : ""}
          </td>
          <td></td>
          <td></td>
        </tr>
        );
      })}
    </>
  );
}

// =============================================================================
// BrokerSowDialog — T-INS-SOW v2 (James's 4 Sep smoke: "slim it down with some
// summaries… site address, lat and long, a snip/photo of the design, high
// level build plan").
//
// The dialog opens with a FACTS GRID filled straight from project data (no AI
// involved) and a high-level program preview. One button drafts the five SHORT
// summary sections; every field is editable; an optional design snip prints on
// the PDF. Nothing automatic: the human reviews, downloads, forwards.
//
// v3 (4 Sep, after live test): the SOW SAVES per project (mig 354,
// project_broker_sows + the snip in storage) — reopening brings back the
// edited sections and facts with zero worker calls; only Redraft hits the AI.
// The program table stays live from the build plan so the PDF keeps up.
// -----------------------------------------------------------------------------
const SOW_SECTIONS: { key: keyof BrokerSowSections; label: string; rows: number }[] = [
  { key: "works_description",        label: "Works summary",           rows: 3 },
  { key: "heights_and_access",       label: "Heights & access",        rows: 2 },
  { key: "plant_and_lifting",        label: "Plant & lifting",         rows: 2 },
  { key: "subcontractors",           label: "Subcontractors",          rows: 2 },
  { key: "key_hazards_and_controls", label: "Key hazards & controls",  rows: 3 },
];

// Facts grid: fixed order, auto-filled from data, every value editable.
const SOW_FACTS: { key: string; label: string }[] = [
  { key: "site",      label: "Site" },
  { key: "address",   label: "Address" },
  { key: "latlong",   label: "Lat / Long" },
  { key: "client",    label: "Client" },
  { key: "structure", label: "Structure" },
  { key: "window",    label: "Works window" },
  { key: "crew",      label: "Crew size" },
];

const SOW_BUCKET = "ap-documents";        // snip lives at broker-sow/<project_id>

function valueBand(v: number): string {
  if (v <= 0) return "";
  if (v < 50_000) return "Under $50k";
  if (v < 100_000) return "$50k – $100k";
  if (v < 250_000) return "$100k – $250k";
  if (v < 500_000) return "$250k – $500k";
  if (v < 1_000_000) return "$500k – $1M";
  return "Over $1M";
}

const sowD = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const dt = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return isNaN(dt.getTime()) ? String(iso) : dt.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

function BrokerSowDialog({ project, lines, onClose }: {
  project: any;
  lines: any[];
  onClose: () => void;
}) {
  const user = useRequireAuth();
  const [facts, setFacts] = useState<Record<string, string>>({});
  const [sections, setSections] = useState<BrokerSowSections | null>(null);
  const [includeBand, setIncludeBand] = useState(false);
  const [includeProgram, setIncludeProgram] = useState(true);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const imgInput = useRef<HTMLInputElement>(null);
  const hydrated = useRef(false);                       // saved-row applied once
  const savedImagePath = useRef<string | null>(null);   // storage path currently on the row
  const loadedImageDataUrl = useRef<string | null>(null); // to detect a changed snip

  const isDirector = hasRole(user, "director");
  const band = valueBand(Number(project?.contract_value ?? 0));

  // Everything the client can already see under its own RLS — gathered on
  // open so the facts grid + program preview fill themselves instantly.
  const { data: gathered, isLoading: gathering } = useQuery({
    queryKey: ["broker_sow_data", project.project_id],
    queryFn: async () => {
      const [bundle, rosterQ, bookingsQ, actsQ] = await Promise.all([
        loadDesignBundle({ projectId: project.project_id }).catch(() => null),
        (supabase as any).from("site_roster")
          .select("employee_id, on_site_from, on_site_to")
          .eq("project_id", project.project_id),
        (supabase as any).from("project_bookings")
          .select("title, booking_type, scheduled_start, scheduled_end, status")
          .eq("project_id", project.project_id)
          .neq("status", "cancelled")
          .order("scheduled_start"),
        (supabase as any).from("project_program_activities")
          .select("name, kind, start_date, end_date, is_key, plant_needed, owner, in_build_plan")
          .eq("project_id", project.project_id)
          .eq("is_active", true)
          .order("start_date"),
      ]);

      const site = project.sites;
      const roster = (rosterQ.data ?? []) as any[];
      const acts = (actsQ.data ?? []) as any[];
      const bookings = (bookingsQ.data ?? []) as any[];

      // ---- facts, straight from data ----
      const address = [site?.address_line1, site?.address_line2, site?.suburb, site?.state, site?.postcode]
        .filter(Boolean).join(", ");
      const latlong = site?.latitude != null && site?.longitude != null
        ? `${Number(site.latitude).toFixed(6)}, ${Number(site.longitude).toFixed(6)}` : "";
      const dsn: any = bundle?.design ?? null;
      const structure = dsn
        ? [dsn.structure_type, dsn.structure_height_m ? `${dsn.structure_height_m} m` : null].filter(Boolean).join(", ")
        : (site?.structure_height_m ? `${site.structure_height_m} m structure` : "");
      const froms = roster.map(r => r.on_site_from).filter(Boolean).sort();
      const tos = roster.map(r => r.on_site_to).filter(Boolean).sort();
      // fall back to the build program span when no roster window exists
      const planActs = acts.filter(a => a.in_build_plan && a.start_date);
      const progFrom = planActs[0]?.start_date ?? null;
      const progTo = planActs.map(a => a.end_date ?? a.start_date).filter(Boolean).sort().slice(-1)[0] ?? null;
      const winFrom = froms[0] ?? progFrom;
      const winTo = tos[tos.length - 1] ?? progTo;
      const crewCount = new Set(roster.map(r => r.employee_id)).size;

      const facts0: Record<string, string> = {
        site:      site?.site_name ?? "",
        address,
        latlong,
        client:    project.clients?.legal_name ?? "",
        structure,
        window:    winFrom ? `${sowD(winFrom)} – ${winTo ? sowD(winTo) : "open"}` : "",
        crew:      crewCount > 0 ? `${crewCount} Coretel personnel` : "",
      };

      // ---- high-level program: build-plan lines, key dates starred ----
      const source = planActs.length > 0 ? planActs : acts.filter(a => a.is_key && a.start_date);
      const program = source.slice(0, 12).map(a => ({
        name: a.name, start: a.start_date ?? null, end: a.end_date ?? null, isKey: !!a.is_key,
      }));

      // ---- extra context lines for the AI draft (facts go in separately) ----
      const extra: string[] = [];
      if (bundle?.design) {
        const dg: any = designDigest(bundle, [site?.site_name, project.project_name]);
        if (dg.summary) extra.push(`Design works summary: ${dg.summary}`);
        if (dg.antennaCount) extra.push(`Antennas in design: ${dg.antennaCount} (${dg.activeAntennaCount ?? 0} active).`);
        if (dg.mwLinks?.length) extra.push(`Microwave links: ${dg.mwLinks.map((l: any) => [l.name, l.farEnd ? `far end ${l.farEnd}` : null, l.dish ? `${l.dish}m dish` : null].filter(Boolean).join(", ")).join("; ")}.`);
      } else {
        extra.push("Design: no design loaded in Ops for this project.");
      }
      const quoted = (lines ?? []).filter((l: any) => l.description);
      if (quoted.length) extra.push("Quoted plant & subcontract lines: " + quoted.slice(0, 20).map((l: any) => l.description).join("; ") + ".");
      const plant = bookings.filter(b => b.booking_type === "plant");
      const outages = bookings.filter(b => b.booking_type === "outage");
      const subbies = bookings.filter(b => b.booking_type === "subcontractor");
      if (plant.length) extra.push("Plant bookings: " + plant.map(b => `${b.title} (${String(b.scheduled_start).slice(0, 10)})`).join("; ") + ".");
      if (outages.length) extra.push("Outage / night works bookings: " + outages.map(b => `${b.title} (${String(b.scheduled_start).slice(0, 10)})`).join("; ") + ".");
      if (subbies.length) extra.push("Subcontractor bookings: " + subbies.map(b => b.title).join("; ") + ".");
      const plantActs2 = [...new Set(acts.flatMap(a => a.plant_needed ?? []))];
      if (plantActs2.length) extra.push("Plant required by the build program: " + plantActs2.join(", ") + ".");

      return { facts: facts0, program, extra };
    },
  });

  // The saved SOW for this project (mig 354) — reopening costs no worker call.
  const savedQ = useQuery({
    queryKey: ["broker_sow_saved", project.project_id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("project_broker_sows")
        .select("*")
        .eq("project_id", project.project_id)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  // Hydrate ONCE when both the saved row and the live gather have landed:
  // saved values win (they're the edited record); gather fills the gaps.
  useEffect(() => {
    if (hydrated.current || savedQ.isLoading || !gathered) return;
    hydrated.current = true;
    const s: any = savedQ.data;
    setFacts(s?.facts && Object.keys(s.facts).length ? s.facts : gathered.facts);
    if (s?.sections) setSections(s.sections);
    if (s) {
      setIncludeBand(!!s.include_band);
      setIncludeProgram(s.include_program !== false);
      setSavedAt(s.updated_at ? new Date(s.updated_at) : null);
    }
    savedImagePath.current = s?.image_path ?? null;
    if (s?.image_path) {
      supabase.storage.from(SOW_BUCKET).download(s.image_path).then(({ data }) => {
        if (!data) return;
        const r = new FileReader();
        r.onload = () => { loadedImageDataUrl.current = String(r.result); setImageDataUrl(String(r.result)); };
        r.readAsDataURL(data);
      }).catch(() => {});
    }
  }, [gathered, savedQ.isLoading, savedQ.data]);

  // Save the whole SOW to the project (upsert — one row per project).
  async function save(opts?: { draftedNow?: boolean }): Promise<boolean> {
    setSaving(true); setErr(null);
    try {
      let imagePath = savedImagePath.current;
      if (imageDataUrl && imageDataUrl !== loadedImageDataUrl.current) {
        const blob = await (await fetch(imageDataUrl)).blob();
        imagePath = `broker-sow/${project.project_id}`;
        const { error: upErr } = await supabase.storage.from(SOW_BUCKET)
          .upload(imagePath, blob, { upsert: true, contentType: blob.type || "image/png" });
        if (upErr) throw upErr;
      } else if (!imageDataUrl && imagePath) {
        await supabase.storage.from(SOW_BUCKET).remove([imagePath]).catch(() => {});
        imagePath = null;
      }
      const { error } = await (supabase as any).from("project_broker_sows").upsert({
        project_id: project.project_id,
        facts,
        sections,
        include_program: includeProgram,
        include_band: includeBand,
        image_path: imagePath,
        updated_by: (user as any)?.user_id ?? null,
        ...(opts?.draftedNow ? { drafted_at: new Date().toISOString() } : {}),
      }, { onConflict: "project_id" });
      if (error) throw error;
      savedImagePath.current = imagePath;
      loadedImageDataUrl.current = imageDataUrl;
      setSavedAt(new Date());
      return true;
    } catch (e: any) {
      setErr(`Couldn't save: ${e?.message ?? e}`);
      return false;
    } finally { setSaving(false); }
  }

  const program = gathered?.program ?? [];

  async function draft() {
    setDrafting(true); setErr(null);
    try {
      const ctx: string[] = [];
      ctx.push(`Project: ${project.project_code} — ${project.project_name}.`);
      SOW_FACTS.forEach(({ key, label }) => { if (facts[key]) ctx.push(`${label}: ${facts[key]}.`); });
      ctx.push(...(gathered?.extra ?? []));
      if (includeBand && band) ctx.push(`Contract value band: ${band}.`);

      const res = await apiFetch("/projects/broker-sow-draft", {
        method: "POST",
        body: JSON.stringify({ context: ctx.join("\n") }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({} as any));
        throw new Error((e as any)?.error === "ai_not_configured" ? "AI drafting isn't configured on the worker." : "Drafting failed — try again.");
      }
      const payload = await res.json() as { sections: BrokerSowSections };
      setSections({ ...payload.sections, contract_value_band: includeBand ? (payload.sections.contract_value_band || band) : "" });
      // persist immediately so the draft can never be lost (mig 354)
      setTimeout(() => { void save({ draftedNow: true }); }, 0);
    } catch (e: any) {
      setErr(e?.message ?? "Drafting failed");
    } finally { setDrafting(false); }
  }

  function pickImage(f: File | undefined) {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setErr("Image too big — keep the snip under 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setImageDataUrl(String(reader.result));
    reader.readAsDataURL(f);
  }

  async function makePdf() {
    if (!sections) return;
    setPdfing(true); setErr(null);
    try {
      await save();   // the saved SOW always matches the last PDF grabbed
      await generateBrokerSowPdf({
        projectCode: project.project_code ?? "",
        projectName: project.project_name ?? "",
        facts: SOW_FACTS.map(({ key, label }) => ({ label, value: (facts[key] ?? "").trim() }))
          .filter(f => f.value !== "")
          .concat(includeBand && (sections.contract_value_band ?? "").trim()
            ? [{ label: "Value band", value: (sections.contract_value_band ?? "").trim() }] : []),
        sections,
        program: includeProgram ? program : [],
        imageDataUrl,
        preparedByName: [ (user as any)?.first_name, (user as any)?.last_name ].filter(Boolean).join(" ") || "Coretel Group",
      });
    } catch (e: any) {
      setErr(e?.message ?? "PDF failed");
    } finally { setPdfing(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card rounded-xl border border-line w-full max-w-3xl mb-10" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <div className="text-[15px] font-semibold text-text">Broker SOW — {project.project_code}</div>
          <div className="text-[12px] text-text-muted mt-1">
            Facts fill straight from Ops. Draft the summaries, tidy anything up, download, forward it yourself.
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* ---- Facts grid: data-driven, every field editable ---- */}
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Site & project facts</div>
            {gathering ? (
              <div className="py-4 flex justify-center"><Spinner /></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {SOW_FACTS.map(({ key, label }) => (
                  <label key={key} className="block">
                    <span className="text-[10px] uppercase tracking-wider text-text-dim">{label}</span>
                    <input
                      value={facts[key] ?? ""}
                      onChange={(e) => setFacts({ ...facts, [key]: e.target.value })}
                      className="mt-0.5 w-full bg-ink-raised border border-line rounded-md px-2.5 py-1.5 text-[12px] text-text"
                    />
                  </label>
                ))}
                {isDirector && band && (
                  <label className="flex items-center gap-2 text-[12px] text-text mt-4">
                    <input type="checkbox" className="accent-accent" checked={includeBand} onChange={(e) => setIncludeBand(e.target.checked)} />
                    Include value band ({band}) — director only
                  </label>
                )}
              </div>
            )}
          </div>

          {/* ---- High-level program preview ---- */}
          {program.length > 0 && (
            <div className="border border-line rounded-md p-3">
              <label className="flex items-center gap-2 text-[12px] text-text font-medium">
                <input type="checkbox" className="accent-accent" checked={includeProgram} onChange={(e) => setIncludeProgram(e.target.checked)} />
                High-level program on the PDF ({program.length} lines)
              </label>
              {includeProgram && (
                <div className="mt-2 space-y-0.5 max-h-36 overflow-y-auto">
                  {program.map((r: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-[11px]">
                      <span className={r.isKey ? "text-accent" : "text-text-dim"}>{r.isKey ? "★" : "·"}</span>
                      <span className="text-text flex-1 truncate">{r.name}</span>
                      <span className="text-text-muted font-mono">{sowD(r.start)}{r.end && r.end !== r.start ? ` – ${sowD(r.end)}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- Design snip / cover photo ---- */}
          <div className="flex items-center gap-3">
            <input ref={imgInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => { pickImage(e.target.files?.[0]); e.currentTarget.value = ""; }} />
            {imageDataUrl ? (
              <>
                <img src={imageDataUrl} alt="design snip" className="h-14 rounded border border-line" />
                <span className="text-[12px] text-text-muted flex-1">Design snip — prints under the facts</span>
                <button type="button" onClick={() => setImageDataUrl(null)}
                  className="text-[11px] text-text-muted hover:text-danger">remove</button>
              </>
            ) : (
              <button type="button" onClick={() => imgInput.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                <FileText size={13} /> Add a design snip / photo (optional)
              </button>
            )}
          </div>

          {/* ---- Summaries: one Draft button, then five SHORT editable boxes ---- */}
          {!sections ? (
            <Button icon={FileText} onClick={() => void draft()} disabled={drafting || gathering}>
              {drafting ? "Drafting summaries…" : "Draft the summaries"}
            </Button>
          ) : (
            <div className="space-y-3">
              {SOW_SECTIONS.map(({ key, label, rows }) => (
                <label key={key} className="block">
                  <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">{label}</span>
                  <textarea
                    value={(sections[key] as string) ?? ""}
                    onChange={(e) => setSections({ ...sections, [key]: e.target.value })}
                    rows={rows}
                    className="mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text leading-relaxed"
                  />
                </label>
              ))}
            </div>
          )}
          {err && <div className="text-[12px] text-danger">{err}</div>}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-line">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            {savedAt && (
              <span className="text-[11px] text-text-dim">
                Saved {savedAt.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {(sections || Object.keys(facts).length > 0) && (
              <Button variant="ghost" size="sm" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
            {sections && (
              <Button variant="secondary" size="sm" onClick={() => void draft()} disabled={drafting}>
                {drafting ? "Redrafting…" : "Redraft"}
              </Button>
            )}
            {sections && (
              <Button size="sm" icon={FileText} onClick={() => void makePdf()} disabled={pdfing}>
                {pdfing ? "Generating…" : "Download PDF"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ComponentStatementDialog — pick the quoted plant/subcontract lines, tie them
// to a real claim invoice, generate the client-evidence PDF. (28 Aug 2026)
// =============================================================================
// The Ventia hoop: they show NBN "the subcontractor's invoice" - which is
// Coretel's own pricing for the plant component, at the QUOTED amounts, never
// the actual supplier cost. This never touches Xero; the PDF references the
// real claim so it can't be double-processed. Full reasoning in the ticket
// (claude/2026-08-28_TICKET_component_statements_for_client_evidence).
// =============================================================================
const STMT_UNIT_LABELS: Record<string, string> = {
  hour: "hr", day: "day", week: "wk", night: "night", kilometre: "km",
  linear_metre: "lm", cubic_metre: "m³", square_metre: "m²", kilogram: "kg",
  tonne: "t", each: "ea", lump_sum: "LS", litre: "L",
};

function ComponentStatementDialog({ project, lines, quoteNoById, claims, onClose }: {
  project: any;
  lines: any[];
  quoteNoById: Map<string, string>;
  claims: any[];
  onClose: () => void;
}) {
  const user = useRequireAuth();
  // Plant lines are the reason this exists - pre-tick them. Everything else is
  // offered, unticked (a subcontract line lives under whatever category the
  // quote gave it, so the manager decides).
  const [picked, setPicked] = useState<Set<string>>(() => new Set(
    lines.filter(l => (l.category ?? "").toLowerCase() === "plant").map(l => l.quote_line_id)
  ));
  const [claimId, setClaimId] = useState<string>(claims[0]?.id ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (id: string) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const chosen = lines.filter(l => picked.has(l.quote_line_id));
  const total = chosen.reduce((a, l) => a + Number(l.line_total_ex_gst ?? 0), 0);
  const claim = claims.find(c => c.id === claimId);

  // Group the pick-list by source quote so VAR lines read under their VAR.
  const byQuote = new Map<string, any[]>();
  for (const l of lines) {
    const k = l.quote_id;
    if (!byQuote.has(k)) byQuote.set(k, []);
    byQuote.get(k)!.push(l);
  }

  async function generate() {
    if (!claim) { setErr("Pick the claim invoice this component belongs to."); return; }
    if (chosen.length === 0) { setErr("Tick at least one line."); return; }
    setBusy(true);
    setErr(null);
    try {
      const stmtLines: ComponentStatementLine[] = chosen.map(l => ({
        source: quoteNoById.get(l.quote_id) ?? "quote",
        code: l.rate_line?.line_code ?? null,
        description: l.description ?? "",
        detail: l.description_secondary ?? null,
        quantity: Number(l.quantity ?? 0),
        unit: STMT_UNIT_LABELS[l.unit] ?? (l.unit ?? ""),
        rate_ex_gst: Number(l.sell_price_ex_gst ?? 0),
        amount_ex_gst: Number(l.line_total_ex_gst ?? 0),
      }));
      await generateComponentStatementPdf({
        projectCode: project.project_code ?? "",
        projectName: project.project_name ?? "",
        siteName: project.site?.site_name ?? project.sites?.site_name ?? null,
        clientName: project.client?.legal_name ?? project.clients?.legal_name ?? "Client",
        refInvoiceNumber: claim.invoice_number,
        refInvoiceDate: claim.invoice_date_sent ?? null,
        lines: stmtLines,
        note: note.trim() || null,
        preparedByName: [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Coretel Group",
      });
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-2xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
          <div>
            <div className="text-[14px] font-semibold text-text">Plant & subcontract component statement</div>
            <div className="text-[11px] text-text-muted mt-0.5">
              Quoted amounts only · references a real claim · never touches Xero
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-ink-hover text-text-muted"><X size={16}/></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Forms part of claim invoice</span>
            <select value={claimId} onChange={e => setClaimId(e.target.value)}
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text">
              <option value="">— pick the invoice this component sits inside —</option>
              {claims.map(c => (
                <option key={c.id} value={c.id}>
                  {c.invoice_number} · {fmtDate(c.invoice_date_sent)} · {fmtMoney(c.invoice_amount)}
                </option>
              ))}
            </select>
            {claims.length === 0 && (
              <span className="text-[11px] text-warn">No client invoices issued yet — issue the claim first, then generate its component statement.</span>
            )}
          </label>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">
              Lines to include <span className="normal-case">(plant lines pre-ticked)</span>
            </div>
            <div className="border border-line rounded divide-y divide-line/40 max-h-72 overflow-y-auto">
              {Array.from(byQuote.entries()).map(([qid, qLines]) => (
                <div key={qid}>
                  <div className="px-3 py-1.5 bg-ink-raised/50 text-[10px] font-mono text-text-muted sticky top-0">
                    {quoteNoById.get(qid) ?? "quote"}
                  </div>
                  {qLines.map(l => (
                    <label key={l.quote_line_id}
                      className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-ink-raised/30">
                      <input type="checkbox" className="accent-accent flex-shrink-0"
                        checked={picked.has(l.quote_line_id)} onChange={() => toggle(l.quote_line_id)} />
                      <span className="text-[12px] text-text truncate flex-1 min-w-0">
                        {l.description}
                        {(l.category ?? "").toLowerCase() === "plant" && (
                          <span className="ml-1.5 text-[9px] uppercase font-mono px-1 rounded bg-accent/15 text-accent">plant</span>
                        )}
                      </span>
                      <span className="text-[11px] font-mono text-text-muted whitespace-nowrap">{fmtMoney(l.line_total_ex_gst)}</span>
                    </label>
                  ))}
                </div>
              ))}
              {lines.length === 0 && (
                <div className="px-3 py-3 text-[12px] text-text-dim">No contracted quote lines found for this project.</div>
              )}
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Note on the document (optional)</span>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="e.g. Hydro excavation delta covered under VAR-26-0016 as agreed"
              className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
          </label>

          {err && <div className="text-[11px] text-danger">{err}</div>}
        </div>

        <div className="flex items-center justify-between px-5 py-3.5 border-t border-line">
          <span className="text-[12px] font-mono text-text-muted">
            {chosen.length} line{chosen.length === 1 ? "" : "s"} · <span className="text-text font-semibold">{fmtMoney(total)}</span> ex GST
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" icon={FileText} disabled={busy || chosen.length === 0 || !claimId}
              onClick={generate}>
              {busy ? "Generating…" : "Generate PDF"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EditScheduleDialog — manager+ edits all four project dates at once.
// T-077 — James 2026-04-25. Single dialog avoids per-field inline-edit
// clutter; matches the "Add MOP" / "Replace master" dialog pattern used
// elsewhere. Validation: end >= start (both pairs).
// =============================================================================
function EditScheduleDialog({ project, onClose, onSaved }: {
  project: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [scheduledStart, setScheduledStart] = useState<string>(project.scheduled_start_date ?? "");
  const [scheduledEnd,   setScheduledEnd]   = useState<string>(project.scheduled_end_date ?? "");
  const [actualStart,    setActualStart]    = useState<string>(project.actual_start_date ?? "");
  const [actualEnd,      setActualEnd]      = useState<string>(project.actual_end_date ?? "");
  // T-290 — acceptance_received_at is timestamptz; convert to YYYY-MM-DD for
  // the date input. Sent back as a date string, Postgres coerces to timestamp.
  const [acceptanceReceived, setAcceptanceReceived] = useState<string>(
    project.acceptance_received_at ? String(project.acceptance_received_at).slice(0, 10) : "",
  );
  const [workingHeight,  setWorkingHeight]  = useState<string>(
    project.working_height_m == null ? "" : String(project.working_height_m)
  );
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<string | null>(null);

  function validate(): string | null {
    if (scheduledStart && scheduledEnd && scheduledEnd < scheduledStart) {
      return "Scheduled end must be the same as or after scheduled start.";
    }
    if (actualStart && actualEnd && actualEnd < actualStart) {
      return "Actual end must be the same as or after actual start.";
    }
    return null;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setErr(v); return; }
    setBusy(true);
    setErr(null);
    try {
      const { error } = await supabase
        .from("projects")
        .update({
          scheduled_start_date:    scheduledStart || null,
          scheduled_end_date:      scheduledEnd   || null,
          actual_start_date:       actualStart    || null,
          actual_end_date:         actualEnd      || null,
          // T-290 — capture acceptance date here so it can be set independently
          // of closing. Date input → Postgres timestamptz coercion.
          acceptance_received_at:  acceptanceReceived || null,
          working_height_m:        workingHeight.trim() === "" ? null : parseFloat(workingHeight),
        })
        .eq("project_id", project.project_id);
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to save dates.");
    } finally {
      setBusy(false);
    }
  }

  function clearAll() {
    setScheduledStart("");
    setScheduledEnd("");
    setActualStart("");
    setActualEnd("");
    setAcceptanceReceived("");
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-16 px-4 overflow-y-auto"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="text-[15px] font-semibold text-text">Edit project schedule</div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-1 rounded hover:bg-ink-hover text-text-muted disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="text-[11px] text-text-muted mb-4">
          {project.project_code} — {project.project_name}
        </div>

        {err && (
          <div className="mb-3 px-3 py-2 bg-danger/12 border border-danger/30 rounded text-[12px] text-danger">{err}</div>
        )}

        {/* Two columns: Scheduled (planning) and Actual (reality) */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <DateInputField label="Scheduled start" value={scheduledStart} onChange={setScheduledStart} />
          <DateInputField label="Scheduled end"   value={scheduledEnd}   onChange={setScheduledEnd} />
          <DateInputField label="Actual start"    value={actualStart}    onChange={setActualStart} />
          <DateInputField label="Actual end"      value={actualEnd}      onChange={setActualEnd} />
        </div>

        {/* T-290 — acceptance date is its own row so it stands out + has space
            for the helper text. Required before status can move to 'closed'. */}
        <div className="mb-3">
          <DateInputField
            label="Client accepted"
            value={acceptanceReceived}
            onChange={setAcceptanceReceived}
          />
          <div className="text-[10px] text-text-dim mt-1">
            When the client formally signed off this project. Required before the project can be closed.
          </div>
        </div>

        {/* Optional working height — for crew + machine planning. Stored on
            the project so it can differ from the structure's overall height
            (e.g. install at 27m on a 48m tower). */}
        <div className="mb-3">
          <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1">
            Working height (m)
          </label>
          <input
            type="number"
            step="0.01"
            value={workingHeight}
            onChange={e => setWorkingHeight(e.target.value)}
            placeholder="Optional — e.g. 27.10"
            className="w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm font-mono outline-none focus:border-accent"
          />
          <div className="text-[10px] text-text-dim mt-1">
            Leave blank if not yet known. Site's overall structure height is set on the site, this is project-specific.
          </div>
        </div>

        <div className="text-[10px] text-text-dim mb-4 leading-snug">
          Leave a date field blank to clear it. Actuals will continue to auto-fill on status changes when blank.
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-line">
          <button
            onClick={clearAll}
            disabled={busy}
            className="text-[11px] text-text-muted hover:text-text underline disabled:opacity-50"
          >
            Clear all
          </button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={handleSave} disabled={busy}>
              {busy ? "Saving…" : "Save dates"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DateInputField({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-text-muted font-medium mb-1">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text font-mono"
      />
    </div>
  );
}

// =============================================================================
// ProjectMilestones — T-079. Free-text name + date, mgr+ writes, all-team reads.
// Mounted inside the Schedule card on the Overview tab. Sorted by date asc.
// Past milestones get a softer treatment so the next-up one is easy to spot.
// =============================================================================
interface MilestoneRow {
  milestone_id:   string;
  project_id:     string;
  milestone_name: string;
  milestone_date: string;
  notes:          string | null;
}

function ProjectMilestones({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ["project_milestones", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_milestones")
        .select("milestone_id, project_id, milestone_name, milestone_date, notes")
        .eq("project_id", projectId)
        .order("milestone_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MilestoneRow[];
    },
  });

  const today = new Date().toISOString().slice(0, 10);

  async function handleAdd() {
    setErr(null);
    if (!newName.trim()) { setErr("Milestone name is required."); return; }
    if (!newDate)        { setErr("Pick a date."); return; }
    setBusy(true);
    try {
      const { error } = await supabase
        .from("project_milestones")
        .insert({
          project_id:     projectId,
          milestone_name: newName.trim(),
          milestone_date: newDate,
        });
      if (error) throw error;
      setNewName("");
      setNewDate("");
      qc.invalidateQueries({ queryKey: ["project_milestones", projectId] });
      qc.invalidateQueries({ queryKey: ["project_milestones_for_calendar", projectId] });
    } catch (e: any) {
      setErr(e?.message ?? "Failed to add milestone.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(milestoneId: string) {
    if (!confirm("Remove this milestone?")) return;
    const { error } = await supabase
      .from("project_milestones")
      .delete()
      .eq("milestone_id", milestoneId);
    if (error) {
      alert(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ["project_milestones", projectId] });
    qc.invalidateQueries({ queryKey: ["project_milestones_for_calendar", projectId] });
  }

  return (
    <div className="mt-4 pt-4 border-t border-line">
      <div className="flex items-center justify-between gap-2 mb-2">
        <SectionHeading icon={ChevronRight}>Milestones</SectionHeading>
        <span className="text-[10px] text-text-dim font-mono">{milestones.length}</span>
      </div>

      {isLoading ? (
        <div className="text-[11px] text-text-muted italic">Loading…</div>
      ) : milestones.length === 0 ? (
        <div className="text-[11px] text-text-dim italic">
          No milestones yet. {canManage ? "Add the first one below." : "Manager will add key dates here."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {milestones.map(m => {
            const past = m.milestone_date < today;
            return (
              <li
                key={m.milestone_id}
                className={`flex items-center justify-between gap-3 py-1.5 px-2 rounded ${
                  past ? "opacity-60" : "bg-ink-raised"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="font-mono text-[12px] text-accent flex-shrink-0 w-[88px]">
                    {fmtDate(m.milestone_date)}
                  </span>
                  <span className="text-[13px] text-text truncate" title={m.milestone_name}>
                    {m.milestone_name}
                  </span>
                </div>
                {canManage && (
                  <button
                    onClick={() => handleDelete(m.milestone_id)}
                    className="text-text-dim hover:text-danger p-1 rounded transition-colors flex-shrink-0"
                    title="Remove milestone"
                  >
                    <Trash2 size={12}/>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add form — manager+ only. Inline so adding 5 milestones in a row
          doesn't require opening a dialog 5 times. */}
      {canManage && (
        <div className="mt-3 pt-3 border-t border-line">
          {err && (
            <div className="mb-2 px-2 py-1.5 bg-danger/12 border border-danger/30 rounded text-[11px] text-danger">{err}</div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Milestone name (e.g. Civils complete, EWP on site, Carrier RFS)"
              className="flex-1 min-w-[200px] bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) handleAdd(); }}
            />
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text font-mono"
            />
            <Button onClick={handleAdd} disabled={busy} size="sm">
              {busy ? "Adding…" : "Set"}
            </Button>
          </div>
          <div className="text-[10px] text-text-dim mt-1.5 italic">
            Free-text for now — recurring names will become a preset list once we see what people log.
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================
function DateCell({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">{label}</div>
      <div className="text-[13px] font-mono text-text mt-1">{value ? fmtDate(value) : <span className="text-text-dim">—</span>}</div>
    </div>
  );
}
function Kv({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">{label}</div>
      <div className={`text-[13px] text-text mt-1 ${mono ? "font-mono" : ""}`}>{value ?? <span className="text-text-dim">—</span>}</div>
    </div>
  );
}
function SectionHeading({ children, icon: Icon }: { children: React.ReactNode; icon?: any }) {
  return (
    <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5">
      {Icon && <Icon size={13} />}{children}
    </div>
  );
}
function StatCard({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <Card>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">{label}</div>
        <div className="text-[24px] font-mono text-text mt-2">{value}</div>
        {hint && <div className="text-[10px] text-text-dim mt-1 italic">{hint}</div>}
      </div>
    </Card>
  );
}

export default ProjectDetailScreen;
